// Component: StrategyBuilder.jsx
// Purpose: Visual strategy builder + backtest launcher/results panel; surfaces backtest
//          failures to the user via snackbar (issue equity-backtest-resolver-fix, Change 2).
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNodeFactory } from './hooks/useNodeFactory';
import StrategyService from '../../../../api/services/strategies';
import { services } from '../../../../services/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useStateContext } from '../../../../contexts/ContextProvider';

import { 
  FiInfo, 
  FiClock,
  FiBarChart2, 
  FiCalendar,
  FiCpu, 
  FiSliders,
  FiChevronRight,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiX,
  FiSettings,
  FiZap,
  FiBell,
  FiActivity,
  FiGitMerge,
  FiLayers,
  FiTrendingUp,
  FiTrendingDown,
  FiPlay,
  FiSquare,
  FiMaximize2,
  FiMinimize2,
  FiAlertTriangle
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from '../../../../components/ds';
import InstrumentSelector from '../../../../components/common/InstrumentSelector';
import HistoricalDataTimeline from '../../../Admin/MarketDataManagement/components/HistoricalDataTimeline';
import SmartChart from '../../../../components/SmartChart/SmartChart';
import StrategyLogicStep from './components/StrategyLogicStep';
import StrategyToolbox from './components/StrategyToolbox';
import NodePropertiesPanel from './components/NodePropertiesPanel';
import CanvasToolbar from './components/CanvasToolbar';
import StrategyTreeView, { SharedByTriggersNotice } from './components/StrategyTreeView';
import RDWatchDashboard from './components/RDWatchDashboard';
import TradeExecutionStep from './components/TradeExecutionStep';
import BacktestStepRight from './components/BacktestStepRight';
import DeploymentSection from './components/DeploymentSection';
import ReadinessPanel from './components/ReadinessPanel';
import { useNodesState, useEdgesState } from 'reactflow';
import WebSocketManager from '../../../../websocket/WebSocketManager';
import { useGlobalWebSocket, useWebSocketUser } from '../../../../context/WebSocketProvider';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { LAYOUT_CONFIG, getLayoutCategory, calculateNodeHeight, NODE_HEIGHTS, PADDING_Y } from './layoutConfig';
import { resolveSmartLabel, isModelNode, categorizeNodes, sanitizeNodesForSync, mirrorExecutionQtyToData, DERIVATIVE_INSTRUMENT_TYPES, CALCULATED_VALUES_CATEGORY, deriveLiveNodeInvalidReasons, keepIfSameReasons } from './utils/nodeUtils';
import { deriveVisualLayers, deriveStrategyIndicators, applyVisualOverrides, pickVisualMembers } from './utils/chartDataDerivation';
import {
  useVisualOverridesStore,
  VisualOverridesContext,
  VisualOverridesNotice,
  displayLockHintFor,
} from '../../../../hooks/useVisualOverrides';
import { isPrimaryInputHandle, sanitizeIncomingEdgesForIndicatorNodes } from './utils/edgeSelection';
import { normalizeTrades } from './utils/backtestUtils';
import { extractDriftConflict } from '../../../../utils/apiErrorUtils';
import { ChartSyncService } from '../../../../components/SmartChart/utils';
import { getExchangeTimezoneSync, formatDateInTimezone } from '../../../../utils/timezone';
import { buildMarkersFromTrades } from '../../../../utils/tradeMarkerBuilder';
import buildBuilderTreeModel from './utils/builderTreeModel';
import useCanvasViewPrefs from './hooks/useCanvasViewPrefs';
import useGraphWiring from './hooks/useGraphWiring';
import { filterToolboxIndicatorsByRole } from './utils/toolboxCatalogue';

export { filterToolboxIndicatorsByRole } from './utils/toolboxCatalogue';

//! broker-qty-semantics (iter-7) — DERIVATIVE_INSTRUMENT_TYPES is now the shared SSOT in
//! ./utils/nodeUtils (imported above), consumed by BOTH this file (`isFnO`) and
//! components/TradeExecutionStep.jsx (OrderCard expired-lot hint). iter-7 re-adds a continuous-mode
//! branch to the instrument-metadata refetch effect below; that branch rolls ONLY on the tighter
//! {FUTURE, OPTION} set (the backend gate's _ROLLABLE_INSTRUMENT_TYPES), not this broader set.


/**
 * Returns comprehensive list of fields based on known backend OHLCV schema.
 * The extractFieldValue function in SmartChart handles missing fields gracefully.
 * 
 * Standard fields come from backend (TimescaleDB OHLCV schema)
 * Calculated fields are always available (computed client-side)
 */
// Default initial graph: Data Source + Candle Chart Only
const EMPTY_LIVE_NODE_RESULTS = Object.freeze({});

const initialLogicNodes = [
  { id: '1', type: 'data_source', position: { x: 50, y: 500 }, data: { label: 'Data Source', timeframe: '5m', field: 'CLOSE' } },
  
  // Default Visualization: Candlestick (Pushed Way Down)
  { 
      id: 'vis_candle', 
      type: 'candle_node', 
      position: { x: 500, y: 1000 }, 
      data: { label: 'Candle', upColor: '#00C292', downColor: '#E46A76', sourceId: '1', input: '1' } 
  },
  
  // Chart Container (Beside Visualization, Bottom Row)
  { id: 'chart_main', type: 'chart_output', position: { x: 1000, y: 1400 }, data: { panel: 'main', id: 'AUTO' } }
];

// Edges connecting Visualizations to Chart
const initialLogicEdges = [
  // Input Edges (Data Source -> Charts)
  { id: 'e_1_vis_candle', source: '1', target: 'vis_candle', animated: true, style: { strokeDasharray: '5,5' } },

  // Output Edges (Charts -> Main Display)
  { id: 'e_candle_chart', source: 'vis_candle', target: 'chart_main', animated: true }
];

const STEPS = [
  { id: 'general', title: 'General Info', icon: FiInfo, description: 'Name & Description' },
  { id: 'instruments', title: 'Instruments & Data', icon: FiBarChart2, description: 'Select Assets' },
  { id: 'logic', title: 'Strategy Logic', icon: FiCpu, description: 'Define Indicators' },
  { id: 'execution', title: 'Trade Execution', icon: FiZap, description: 'Position Sizing & Quantities' }
];

//--> Compute auto-zoom to last 30 days when backtest spans a long period.
//    Called inline when setting results to avoid a separate render cycle that
//    causes SmartChart to fetch OHLC data twice (once for full range, once for 30d).
//    IMPORTANT: Anchor to backtest end date (not last trade time), otherwise
//    sparse strategies can collapse the view to early-run days only.
const computeSafeZoomWindow = (trades, runStartStr, runEndStr, exchangeTz) => {
  const MAX_DAYS = 30; // 30 days is safe for 1m data charting
  
  let globalStart = Date.now();
  let globalEnd = Date.now();

  if (runStartStr && runEndStr) {
    globalStart = new Date(runStartStr).getTime();
    globalEnd = new Date(runEndStr).getTime();
  }
  
  let maxTradeTime = undefined;
  if (trades && trades.length > 0) {
    const times = trades
      .map(t => {
        const ts = t.exitTime || t.exit_time || t.entryTime || t.entry_time;
        return ts ? new Date(ts).getTime() : NaN;
      })
      .filter(t => !isNaN(t));
      
    if (times.length > 0) {
      maxTradeTime = Math.max(...times);
    }
  }
  
  let maxTime = maxTradeTime || globalEnd;
  if (runEndStr) {
    maxTime = Math.min(maxTime, globalEnd);
  }

  const minStartByDuration = maxTime - (MAX_DAYS * 86400000);
  let finalStartMs = minStartByDuration;
  if (runStartStr) {
    finalStartMs = Math.max(minStartByDuration, globalStart);
  }
  
  const expandedEndMs = Math.min(finalStartMs + (MAX_DAYS * 86400000), globalEnd);

  return {
    start: formatDateInTimezone(finalStartMs, exchangeTz),
    end: formatDateInTimezone(expandedEndMs, exchangeTz)
  };
};

export const canEditNodeForRole = (node, isSuperuser = false) => (
  isSuperuser || !isModelNode(node)
);

//--> Used while no tree surface is visible: the model of an empty graph, so consumers keep the exact shape.
const EMPTY_TREE_MODEL = buildBuilderTreeModel([], [], []);

// Position/selection-only React Flow updates do not change the derived tree structure. Keeping the
// previous node array for the notice-only path avoids rebuilding the full tree during canvas drags.
const sameTreeModelInputs = (previous = [], next = []) => (
  previous.length === next.length
  && previous.every((node, index) => {
    const nextNode = next[index];
    if (!nextNode) return false;
    return node.id === nextNode.id
      && node.type === nextNode.type
      && node.data === nextNode.data
      && node.connectable === nextNode.connectable
      && node.deletable === nextNode.deletable;
  })
);

export const lockModelNodesForRole = (nodes = [], isSuperuser = false) => (
  isSuperuser
    ? nodes
    : nodes.map((node) => (isModelNode(node) ? {
      ...node,
      draggable: false,
      deletable: false,
      connectable: false,
    } : node))
);

export const filterNodeChangesByRole = (
  changes = [], nodes = [], isSuperuser = false, edges = [],
) => {
  if (isSuperuser) return changes;
  const lockedIds = new Set(nodes.filter(isModelNode).map((node) => node.id));
  const protectedRemovalIds = new Set(lockedIds);
  edges.forEach((edge) => {
    if (lockedIds.has(edge.source) || lockedIds.has(edge.target)) {
      protectedRemovalIds.add(edge.source);
      protectedRemovalIds.add(edge.target);
    }
  });
  return changes.filter((change) => (
    !lockedIds.has(change.id)
    && !(change.type === 'remove' && protectedRemovalIds.has(change.id))
  ));
};

export const preserveModelNodesForRole = (
  currentNodes = [], proposedNodes = [], isSuperuser = false,
) => {
  if (isSuperuser) return proposedNodes;
  const proposedById = new Map(proposedNodes.map((node) => [node.id, node]));
  const currentIds = new Set(currentNodes.map((node) => node.id));
  const preserved = currentNodes
    .map((node) => (isModelNode(node) ? node : proposedById.get(node.id)))
    .filter(Boolean);
  return [
    ...preserved,
    ...proposedNodes.filter((node) => !currentIds.has(node.id)),
  ];
};

const modelNodeIds = (nodes = []) => new Set(nodes.filter(isModelNode).map((node) => node.id));
const edgeTouchesModel = (edge, lockedIds) => (
  lockedIds.has(edge?.source) || lockedIds.has(edge?.target)
);

export const filterEdgeChangesByRole = (
  changes = [], edges = [], nodes = [], isSuperuser = false,
) => {
  if (isSuperuser) return changes;
  const lockedIds = modelNodeIds(nodes);
  const lockedEdgeIds = new Set(
    edges.filter((edge) => edgeTouchesModel(edge, lockedIds)).map((edge) => edge.id),
  );
  return changes.filter((change) => !lockedEdgeIds.has(change.id));
};

export const preserveModelEdgesForRole = (
  currentEdges = [], proposedEdges = [], nodes = [], isSuperuser = false,
) => {
  if (isSuperuser) return proposedEdges;
  const lockedIds = modelNodeIds(nodes);
  const currentLocked = new Map(
    currentEdges
      .filter((edge) => edgeTouchesModel(edge, lockedIds))
      .map((edge) => [edge.id, edge]),
  );
  const next = proposedEdges
    .filter((edge) => (
      !edgeTouchesModel(edge, lockedIds) || currentLocked.has(edge.id)
    ))
    .map((edge) => currentLocked.get(edge.id) || edge);
  const nextIds = new Set(next.map((edge) => edge.id));
  return [
    ...next,
    ...[...currentLocked.values()].filter((edge) => !nextIds.has(edge.id)),
  ];
};

export const reconcileIndicatorOutputs = (nodes = [], metadata = []) => nodes.map((node) => {
  if (node.type !== 'indicator' || !node.data?.indicator) return node;
  const meta = metadata.find((entry) => entry.name === node.data.indicator);
  if (!meta || !meta.outputs) return node;
  const currentOutputs = node.data.outputs;
  //! A selected model version's manifest owns its saved output list. Only legacy model
  //  nodes with no outputs receive the generic metadata union.
  if (node.data.indicator === 'model_regime' && Array.isArray(currentOutputs) && currentOutputs.length > 0) {
    return node;
  }
  if (JSON.stringify(currentOutputs) === JSON.stringify(meta.outputs)) return node;
  return { ...node, data: { ...node.data, outputs: meta.outputs } };
});

const StrategyBuilder = () => {
  const { activeMenu } = useStateContext();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlStep = searchParams.get('step');
  const urlRunId = searchParams.get('runId');

  const { user, isAuthenticated } = useAuth();
  const isSuperuser = user?.is_superuser === true;
  const { showSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(urlStep || 'general');
  const { strategyId, versionId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(!!strategyId);
  const [currentParentVersionId, setCurrentParentVersionId] = useState(versionId || null);

  // --> R&D Mode States

  const [latestData, setLatestData] = useState({});
  const [hoverData, setHoverData] = useState({});
  const [crosshairPos, setCrosshairPos] = useState(null);
  const [activeChartType, setActiveChartType] = useState('candle'); // Control which chart layer is visible (initially candle)

  // --> Backtest Mode States
  const [backtestConfig, setBacktestConfig] = useState({
      startDate: '',
      endDate: '',
      initialCapital: 100000,
      //--> 144-6 R5: no commission input; '' = account default broker (server resolves), else an explicit broker key.
      broker: '',
      slippage: 0.05,
      forceRecalculate: false,
      recalcFromTimestamp: '',
  });
  const [isBacktesting, setIsBacktesting] = useState(false);
  const [backtestProgress, setBacktestProgress] = useState(0);
  const [backtestStage, setBacktestStage] = useState("Initializing...");
  const [backtestResults, setBacktestResults] = useState(null);
  const [isBacktestStale, setIsBacktestStale] = useState(false);
  const [backtestLogs, setBacktestLogs] = useState([]);
  const [backtestRightPanelTab, setBacktestRightPanelTab] = useState('period');
  const [backtestSelectedTimeRange, setBacktestSelectedTimeRange] = useState(null);
  const [isMetricsExpanded, setIsMetricsExpanded] = useState(() => localStorage.getItem('dee_metrics_expanded') === 'true');
  const [isChartExpanded, setIsChartExpanded] = useState(false);

  //--> Debounced period selection: prevents duplicate API calls when user clicks
  //    the same period row rapidly (turnaround is 5-10s, so users tend to click twice).
  const lastPeriodSelectRef = useRef(null);
  const handlePeriodSelect = useCallback((range) => {
      if (!range) { lastPeriodSelectRef.current = null; setBacktestSelectedTimeRange(null); return; }
      const key = `${range.start}|${range.end}`;
      if (key === lastPeriodSelectRef.current) return; // Same range — skip
      lastPeriodSelectRef.current = key;
      setBacktestSelectedTimeRange(range);
      //--> Allow retry of the same period after 15s (navigation may have failed/timed out)
      setTimeout(() => { if (lastPeriodSelectRef.current === key) lastPeriodSelectRef.current = null; }, 15000);
  }, []);

  const handleToggleMetricsExpanded = () => {
      setIsChartExpanded(false); // Collapse chart when expanding metrics
      setIsMetricsExpanded(prev => {
          const next = !prev;
          localStorage.setItem('dee_metrics_expanded', String(next));
          return next;
      });
  };

  const handleToggleChartExpanded = () => {
      setIsMetricsExpanded(false); // Collapse metrics when expanding chart
      setIsChartExpanded(prev => !prev);
  };

  const { subscribeGlobal, isConnected, isWsReady } = useGlobalWebSocket();
  const { userId } = useWebSocketUser();
  const [activeRunId, setActiveRunId] = useState(null);
  const [isRightPaneCollapsed, setIsRightPaneCollapsed] = useState(false);
  const unsubRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const latestBacktestLoadedRef = useRef(false);
  //! Tracks whether WS delivered at least one progress update in the last poll window.
  //! Ref (not state) so the polling closure reads the latest value without re-mounting.
  const wsProgressReceivedRef = useRef(false);
  //--> Dedup guard: prevents WS + polling race from calling getResults() twice for the same run.
  const resultsLoadedForRunRef = useRef(null);
  //--> Gates chart data fetch until we know whether a past backtest exists.
  //    Prevents wasted OHLC + indicator fetch for the draft dateRange when
  //    the backtest dateRange is about to override it.
  const [isBacktestCheckDone, setIsBacktestCheckDone] = useState(false);

  // Instrument state is needed by early effects (e.g. exchangeTimezone-driven date defaults)
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [liveTick, setLiveTick] = useState(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [instrumentConfig, setInstrumentConfig] = useState({
      isContinuous: false,
      rolloverDays: 2,
      autoCloseOut: true, //! R4 Hop-1 (money-path #30) — default ON
      //! #140/#141 E5 — per-strategy override of the shared session-time authority
      //  (backend/shared/market_calendar/exchange_hours.py SessionTimes.square_off = close − 30).
      //  Empty string = platform default; NEVER hardcode the numeric default here (R11).
      squareOffTime: "",
      squareOffLeadMinutes: ""
  });

  // --> Exchange timezone for consistent date display across all components
  const exchangeTimezone = useMemo(
    () => getExchangeTimezoneSync(selectedInstrument?.exchange),
    [selectedInstrument?.exchange]
  );

  //--> Shared result loader — called by both WS completion handler and polling fallback.
  //    Ref guard ensures only the first caller proceeds; second silently returns.
  const loadCompletedResults = useCallback(async (runId) => {
    if (resultsLoadedForRunRef.current === runId) return;
    resultsLoadedForRunRef.current = runId;

    try {
        const res = await services.backtesting.getResults(runId);
        const fullResult = res.data?.data || res.data;
        if (!fullResult) {
            resultsLoadedForRunRef.current = null; // allow retry on transient empty response
            return;
        }

        const normalizedTrades = normalizeTrades(fullResult.trades);

        setBacktestResults({
            runId,
            metrics: fullResult.metrics,
            total_return: fullResult.metrics?.total_return ?? fullResult.total_return,
            total_return_pct: fullResult.metrics?.total_return_pct ?? fullResult.total_return_pct,
            initial_capital: fullResult.initial_capital || backtestConfig.initialCapital,
            trades: normalizedTrades,
            chartData: fullResult.equity_curve || [],
            buyHoldCurve: fullResult.buy_hold_curve || [],
        });
        setIsBacktestStale(false);
        setBacktestSelectedTimeRange(
            computeSafeZoomWindow(normalizedTrades, fullResult.start_date, fullResult.end_date, exchangeTimezone)
        );
        setBacktestProgress(100);
    } catch (err) {
        console.error('Failed to load backtest results:', err);
        resultsLoadedForRunRef.current = null; // allow retry
    } finally {
        setIsBacktesting(false);
        setActiveRunId(null);
    }
  }, [exchangeTimezone]);

  // --> Layout & Resize Lifecycle
  // Dispatches a window resize event after sidebar transitions to ensure Charts/Canvas re-calculate
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 600); // Wait for both Sidebar (300ms) and Right Pane (500ms) transitions
    return () => clearTimeout(timer);
  }, [activeMenu, isRightPaneCollapsed, isMetricsExpanded]);


  useEffect(() => {
      activeRunIdRef.current = activeRunId;
  }, [activeRunId]);

  //! Polling fallback for backtest completion when WS progress is not received.
  //  Polls status every 5s while a run is active; fires the same completion flow
  //  as the WS backtest_completed handler so the UI is never permanently stuck.
  useEffect(() => {
    if (!activeRunId || !isBacktesting) return;

    const pollInterval = setInterval(async () => {
        //-->If WS is delivering updates, let it drive; only poll if silent for a cycle
        if (wsProgressReceivedRef.current) {
            wsProgressReceivedRef.current = false; // reset for next poll window
            return;
        }
        try {
            const res = await services.backtesting.getStatus(activeRunId);
            const data = res.data?.data || res.data;
            if (!data) return;

            if (data.status === 'completed') {
                clearInterval(pollInterval);
                loadCompletedResults(activeRunId);
            } else if (data.status === 'failed') {
                clearInterval(pollInterval);
                showSnackbar('Backtest failed: ' + (data.error_message || 'Unknown error'), 'error');
                setIsBacktesting(false);
                setActiveRunId(null);
            } else if (data.progress_pct != null && data.progress_pct > 0) {
                setBacktestProgress(data.progress_pct);
                if (data.stage) setBacktestStage(data.stage);
            }
        } catch (_) { /* ignore transient poll errors */ }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [activeRunId, isBacktesting]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket Subscription for Backtest
  //! Uses isWsReady (not isConnected) so the effect runs only after auth_success,
  //! guaranteeing WebSocketManager.isAuthenticated === true → SUBSCRIBE sent immediately.
  useEffect(() => {
    if (!userId || !isWsReady) return;

    const topic = `user.${userId}.backtest`;
    let resultsAccumulator = null;


    const handleWebSocketMessage = (payload) => {
        const currentRunId = activeRunIdRef.current;
        if (!currentRunId || payload?.run_id !== currentRunId) return;

        if (payload?.type === 'backtest_progress') {
            wsProgressReceivedRef.current = true; //! Signal polling fallback that WS is active
            setBacktestProgress(payload.progress_pct || payload.progress || 0);
            if (payload.stage) setBacktestStage(payload.stage);
            if (payload.log) {
                setBacktestLogs(prev => [...prev, payload.log]);
            }
        } 
        else if (payload?.type === 'backtest_completed') {
            setBacktestProgress(100);

            // Promote local strategy status to backtested immediately (backend does this too)
            setStrategyInfo(prev => {
                if (prev.status === 'draft') return { ...prev, status: 'backtested' };
                return prev;
            });

            loadCompletedResults(payload.run_id);
        }
        else if (payload?.type === 'backtest_error') {
            showSnackbar('Backtest failed: ' + (payload.error || payload.error_message), 'error');
            setIsBacktesting(false);
            setActiveRunId(null);
        }
    };

    const unsub = subscribeGlobal(topic, {
        onData: handleWebSocketMessage
    });
    unsubRef.current = unsub;

    return () => {
         if (unsub) unsub();
         unsubRef.current = null;
    };
  }, [userId, isWsReady, subscribeGlobal]);

  //--> Reset auto-load state when navigating between strategies/versions.
  //    Without this, latestBacktestLoadedRef and backtestResults persist across
  //    tree-view version switches (no component remount), and the guards below
  //    silently skip fetching the new version's results.
  useEffect(() => {
    latestBacktestLoadedRef.current = false;
    setBacktestResults(null);
    setIsBacktestCheckDone(false);
    setIsBacktestStale(false);
  }, [strategyId, versionId]);

  // ─── Auto-load latest backtest results on strategy open ───
  // Fires after strategy load completes (regardless of active step).
  // Sets backtestResults.runId + backtestConfig dates BEFORE chart renders
  // (gated by isBacktestCheckDone) so SmartChart key is stable from the start.
  useEffect(() => {
    if (!strategyId) { setIsBacktestCheckDone(true); return; } // New unsaved strategy
    if (latestBacktestLoadedRef.current) return;
    if (isLoading) return; // Wait for strategy to finish loading
    latestBacktestLoadedRef.current = true;
    if (backtestResults || urlRunId) {
      setIsBacktestCheckDone(true);
      return; // Already have results
    }

    const loadLatestBacktest = async () => {
      console.log(`[StrategyBuilder] ⏳ Auto-loading latest backtest for strategy ${strategyId}`);
      const t0 = performance.now();
      try {
        //--> Phase 1: Fast metadata check — listRuns is a lightweight paginated query.
        //    Use start_date/end_date from the run to set the correct date range BEFORE
        //    fetching full results. This lets SmartChart load OHLC with the right window
        //    immediately, avoiding a wasted draft-dateRange fetch.
        //--> page_size (not limit) — backend param name. Use 5 so runs.find() can
        //    skip failed/cancelled runs and still locate the latest completed one.
        const resp = await services.backtesting.listRuns({ strategy_id: strategyId, page_size: 5 });
        const data = resp.data?.data || resp.data;
        const runs = data?.runs || [];
        console.log(`[StrategyBuilder] ✅ Phase 1 check done in ${(performance.now() - t0).toFixed(2)}ms — ${runs.length} runs`);

        if (runs.length === 0) {
          setIsBacktestCheckDone(true); // No backtest — release chart with draft dates
          return;
        }

        //--> Find the latest COMPLETED run that matches the current version.
        //    A version mismatch means the user changed the strategy (e.g. 1m → 5m)
        //    after the backtest ran. Applying old-version results (wrong timeframe,
        //    different nodes) to the current chart corrupts the display.
        const currentVersionId = currentParentVersionId;
        const run = runs.find(r => r.status === 'completed' && (!currentVersionId || r.version_id === currentVersionId));
        if (!run) {
          const anyCompleted = runs.find(r => r.status === 'completed');
          if (anyCompleted && currentVersionId && anyCompleted.version_id !== currentVersionId) {
            console.warn(`[StrategyBuilder] Latest backtest (version ${anyCompleted.version_id}) doesn't match current version (${currentVersionId}) — skipping stale results`);
            setIsBacktestStale(true);
            //--> Anchor chart to the stale run's date range so the user sees the window
            //    where their backtest ran. We do NOT load results (no backtestResults) —
            //    indicators still compute against the CURRENT graph on the correct dates.
            if (anyCompleted.start_date) {
              const startStr = anyCompleted.start_date.split('T')[0];
              const endStr = anyCompleted.end_date ? anyCompleted.end_date.split('T')[0] : startStr;
              setBacktestConfig(prev => ({ ...prev, startDate: startStr, endDate: endStr }));
            }
          } else {
            console.warn(`[StrategyBuilder] No completed runs in latest ${runs.length} (statuses: ${runs.map(r => r.status).join(', ')})`);
          }
          setIsBacktestCheckDone(true);
          return;
        }

        //--> Use dates to set backtestConfig BEFORE fetching full results.
        const startStr = run.start_date || run.start_time;
        //--> Default end to today when null — keeps fetchDateRange wide so all data
        //    is reachable via pagination. The auto-zoom effect in SmartChart handles
        //    jumping to the right page when a specific period is selected.
        const endStr = run.end_date || run.end_time || new Date().toISOString().split('T')[0];

        if (startStr) {
          setBacktestConfig(prev => ({
            ...prev,
            startDate: startStr.split('T')[0],
            endDate: endStr.split('T')[0]
          }));
        }

        //--> Pre-set replay context (runId only) so the indicator calc request (if any)
        //    uses calc_intent='backtest_replay_view' and hits the Redis response cache.
        //    Full results (trades, metrics) are merged in Phase 2 below.
        setBacktestResults({ runId: run.run_id });

        //--> Phase 2: Fetch full results (trades, metrics)
        const t2 = performance.now();
        console.log(`[StrategyBuilder] ⏳ Phase 2: Fetching full results for run ${run.run_id}...`);
        const resultResponse = await services.backtesting.getResults(run.run_id);
        const resultData = resultResponse.data?.data || resultResponse.data;
        console.log(`[StrategyBuilder] ✅ Phase 2: Fetch returned in ${(performance.now() - t2).toFixed(2)}ms`);

        if (resultData && (resultData.metrics || resultData.trades)) {
          const normalizedTrades = normalizeTrades(resultData.trades);
          setBacktestResults(prev => ({
            ...prev,
            runId: run.run_id,
            metrics: resultData.metrics,
            total_return: resultData.metrics?.total_return ?? resultData.total_return,
            total_return_pct: resultData.metrics?.total_return_pct ?? resultData.total_return_pct,
            initial_capital: resultData.initial_capital,
            trades: normalizedTrades,
            chartData: resultData.equity_curve || [],
            buyHoldCurve: resultData.buy_hold_curve || []
          }));
          setIsBacktestStale(false);
          setBacktestSelectedTimeRange(computeSafeZoomWindow(normalizedTrades, resultData.start_date, resultData.end_date, exchangeTimezone));
          setBacktestProgress(100);

          setBacktestConfig(prev => ({
            ...prev,
            startDate: resultData.start_date ? resultData.start_date.split('T')[0] : prev.startDate,
            endDate: resultData.end_date ? resultData.end_date.split('T')[0] : prev.endDate,
            initialCapital: resultData.initial_capital ?? prev.initialCapital,
            slippage: resultData.slippage_pct ?? prev.slippage,
          }));
        } else {
          console.warn('[StrategyBuilder] Phase 2 returned no metrics or trades — results incomplete');
        }

        //--> Unlock chart NOW that Phase 2 data (like auto zoom trades window) is known.
        //    This avoids an initial wasted massive fetch.
        setIsBacktestCheckDone(true);
        console.log(`[StrategyBuilder] ✅ Auto-load done. Took ${(performance.now() - t0).toFixed(2)}ms`);

      } catch (e) {
        console.error('[StrategyBuilder] Auto-load failed:', e?.message || e);
        setIsBacktestCheckDone(true);
      }
    };
    loadLatestBacktest();
  }, [strategyId, backtestResults, isLoading, urlRunId, currentParentVersionId]);

  // NOTE: Auto-zoom to last 30 days is now computed inline via computeSafeZoomWindow()
  // at each setBacktestResults call site to prevent double OHLC fetch + indicator calc.

  const handleRunBacktest = async () => {
    if (!strategyId) {
        showSnackbar('Save the strategy before running a backtest.', 'warning');
        return;
    }
    setIsBacktesting(true);
    //--> Do NOT clear backtestResults — chart keeps displaying old run (no remount)
    //--> Do NOT clear backtestSelectedTimeRange — prevents dateRange drift
    setIsBacktestStale(false);
    setBacktestLogs([]);
    setBacktestProgress(0);
    setBacktestStage("Initializing...");
    resultsLoadedForRunRef.current = null; // reset dedup guard for new run
    setLogicRightTab('backtest');
    setLogicViewMode('rd');

    try {
        const payload = {
            strategy_id: strategyId,
            version_id: currentParentVersionId,
            start_time: strategyInfo.dateRange?.start || backtestConfig.startDate,
            end_time: strategyInfo.dateRange?.end || backtestConfig.endDate || new Date().toISOString().split('T')[0],
            initial_capital: backtestConfig.initialCapital,
            //--> 144-6 R5-T1: the run's broker picks WHICH managed schedule prices it; omitted = server resolves.
            ...(backtestConfig.broker ? { broker: backtestConfig.broker } : {}),
          slippage: backtestConfig.slippage,
          force_recalculate: !!backtestConfig.forceRecalculate,
          recalc_from_timestamp: backtestConfig.recalcFromTimestamp || null,
        };
        const response = await services.backtesting.run(payload);
        const responseData = response.data?.data || response.data;
        const runId = responseData?.run_id || responseData?.id;
        
        if (runId) {
            setActiveRunId(runId);
        } else {
             setIsBacktesting(false);
        }
    } catch (e) {
        showSnackbar('Failed to start backtest: ' + (e?.response?.data?.detail || e.message), 'error');
        setIsBacktesting(false);
    }
  };

  const handleLoadPreviousRun = async (run) => {
    setIsBacktesting(true);
    //--> Don't clear backtestSelectedTimeRange here — it causes a brief render where
    //--> effectiveDateRange falls back to the full strategy range, triggering a wasted
    //--> OHLC fetch. Instead, set the new zoom window after the API response so all
    //--> state updates (runId + dateRange) are batched in a single render.
    setLogicRightTab('backtest');
    setLogicViewMode('rd');
    try {
      const resultResponse = await services.backtesting.getResults(run.run_id);
      const data = resultResponse.data?.data || resultResponse.data;

      if (data) {
        setBacktestConfig(prev => ({
          ...prev,
          startDate: data.start_date ? data.start_date.split('T')[0] : prev.startDate,
          endDate: data.end_date ? data.end_date.split('T')[0] : prev.endDate,
          initialCapital: data.initial_capital ?? prev.initialCapital,
          slippage: data.slippage_pct ?? prev.slippage,
        }));

        if (data.metrics || data.trades) {
          const normalizedTrades = normalizeTrades(data.trades);
          //--> Set zoom window BEFORE results so effectiveDateRange is correct
          //--> when SmartChart remounts (keyed on backtestResults.runId)
          const runStart = data.start_date ? String(data.start_date).split('T')[0] : null;
          const runEnd = data.end_date ? String(data.end_date).split('T')[0] : new Date().toISOString().split('T')[0];

          if (runStart) {
            setStrategyInfo(prev => ({
              ...prev,
              dateRange: { start: runStart, end: runEnd || runStart }
            }));
          }

          setBacktestSelectedTimeRange(computeSafeZoomWindow(normalizedTrades, data.start_date, data.end_date, exchangeTimezone));
          setBacktestResults({
            runId: data.run_id,
            metrics: data.metrics,
            total_return: data.metrics?.total_return ?? data.total_return,
            total_return_pct: data.metrics?.total_return_pct ?? data.total_return_pct,
            initial_capital: data.initial_capital,
            trades: normalizedTrades,
            chartData: data.equity_curve || [],
            buyHoldCurve: data.buy_hold_curve || []
          });
          setBacktestProgress(100);
          setIsBacktestStale(false);
        }
      }
    } catch (e) {
      console.error('Failed to load past run results', e);
    } finally {
      setIsBacktesting(false);
    }
  };

  // Logic Step State - Hoisted Graph State (Must be declared before loadStrategy useEffect)
  const [nodes, setNodesUnsafe, onNodesChange] = useNodesState(initialLogicNodes);
  const setNodes = useCallback((update) => {
    setNodesUnsafe((currentNodes) => {
      const proposedNodes = typeof update === 'function' ? update(currentNodes) : update;
      return preserveModelNodesForRole(currentNodes, proposedNodes, isSuperuser);
    });
  }, [setNodesUnsafe, isSuperuser]);
  const [edges, setEdgesUnsafe, onEdgesChangeUnsafe] = useEdgesState(initialLogicEdges);
  const graphNodesRef = useRef(nodes);
  graphNodesRef.current = nodes;
  const setEdges = useCallback((update) => {
    setEdgesUnsafe((currentEdges) => {
      const proposedEdges = typeof update === 'function' ? update(currentEdges) : update;
      return preserveModelEdgesForRole(
        currentEdges, proposedEdges, graphNodesRef.current, isSuperuser,
      );
    });
  }, [setEdgesUnsafe, isSuperuser]);
  const onEdgesChange = useCallback((changes) => {
    const allowedChanges = filterEdgeChangesByRole(
      changes, edges, nodes, isSuperuser,
    );
    if (allowedChanges.length > 0) onEdgesChangeUnsafe(allowedChanges);
  }, [onEdgesChangeUnsafe, edges, nodes, isSuperuser]);

  /*>>> 50-8 — ONE display-record store + effective nodes <<<*/
  //! The Builder is a second writer of the SAME per-strategy record Live Analysis edits
  //! (strategies.visual_overrides). `nodes` stays the RAW version graph (computation, save payloads,
  //! readiness); `effectiveNodes` = applyVisualOverrides(raw, saved, draft) is what every DISPLAY
  //! surface renders. Zero GET/PUT while there is no strategyId (unsaved strategy: styling stays in
  //! the local node data and becomes the first version's creation-time default, D6/R5).
  const visualStore = useVisualOverridesStore(strategyId, nodes);
  const effectiveNodes = useMemo(
    () => applyVisualOverrides(nodes, visualStore.overridesSaved, visualStore.draftOverrides),
    [nodes, visualStore.overridesSaved, visualStore.draftOverrides],
  );

  const [selectedLogicNode, setSelectedLogicNode] = useState(null);
  //--> R2-T3 / AC2.3 — the properties panel prefills from the EFFECTIVE node (saved ⊕ draft over the
  //    selected raw node); the raw `selectedLogicNode` state is untouched (computation / identity).
  const effectiveSelectedLogicNode = useMemo(
    () => (selectedLogicNode
      ? applyVisualOverrides([selectedLogicNode], visualStore.overridesSaved, visualStore.draftOverrides)[0]
      : selectedLogicNode),
    [selectedLogicNode, visualStore.overridesSaved, visualStore.draftOverrides],
  );
  
  // Fix #3: LIVE SYNC — ChartSyncService wraps a BroadcastChannel which is a
  // persistent resource. useMemo does NOT guarantee stability across React
  // experimental reconcilers and cannot run cleanup, so we use useRef + useEffect.
  const syncServiceRef = useRef(null);
  useEffect(() => {
      const svc = new ChartSyncService(strategyId || 'default');
      syncServiceRef.current = svc;
      return () => {
          svc.destroy?.();
          syncServiceRef.current = null;
      };
  }, [strategyId]);

  useEffect(() => {
      const syncService = syncServiceRef.current;
      if (!syncService) return;

      // BROADCAST: Send current state to all listeners (Pop-out windows)
      // SANITIZE: Remove functions (onHover, etc) that cause DataCloneError
      const cleanNodes = sanitizeNodesForSync(nodes);
      syncService.emitStructure({ nodes: cleanNodes, edges });

      // HANDLER: Respond to "STRUCTURE_REQUEST" (when a new Pop-out opens)
      syncService.onSetStructureRequest(() => {
          const cleanNodes = sanitizeNodesForSync(nodes);
          syncService.emitStructure({ nodes: cleanNodes, edges });
      });
  }, [nodes, edges]);
  
  // Data Schema State (For Dynamic Field Availability)
  const [dataSchema, setDataSchema] = useState([]);
  
  // Canonical Indicator Metadata (Backend-Driven Protocol)
  const [allIndicatorMetadata, setAllIndicatorMetadata] = useState([]);
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);

  // Fetch Indicator Metadata on Mount
  useEffect(() => {
    const loadMetadata = async () => {
        try {
            const res = await services.indicators.list();
            if (res.data?.status === 'success') {
                setAllIndicatorMetadata(res.data.indicators || []);
            }
        } catch (err) {
            console.error("Failed to load indicator metadata", err);
        } finally {
            setIsMetadataLoading(false);
        }
    };
    loadMetadata();
  }, []);
  
  const toolboxIndicators = useMemo(() => {
    const CUSTOM_UTILITIES = [
      { 
        id: 'field_extractor', 
        name: 'Field Extractor', 
        type: 'indicator', 
        category: CALCULATED_VALUES_CATEGORY, 
        data: { 
          indicator: 'FieldExtractor', 
          label: 'Field Extractor', 
          isFieldExtractor: true, 
          params: { field: 'CLOSE', source: 'CLOSE' } 
        }, 
        color: 'text-warning', 
        bg: 'bg-[var(--badge-warn-bg)]' 
      }
    ];

    const getCategoryStyle = (category) => {
      const cat = category?.toLowerCase() || '';
      if (cat.includes('trend')) return { color: 'text-[var(--theme-purple)]', bg: 'bg-[color-mix(in_oklch,var(--theme-purple)_10%,transparent)]/10' };
      if (cat.includes('momentum')) return { color: 'text-accent', bg: 'bg-accent/10' };
      if (cat.includes('volatility')) return { color: 'text-warning', bg: 'bg-[var(--badge-warn-bg)]' };
      if (cat.includes('volume')) return { color: 'text-profit', bg: 'bg-profit/10' };
      return { color: 'text-fg-3', bg: 'bg-bg-3 dark:bg-bg-1/10' };
    };

    const indicators = allIndicatorMetadata.map(ind => ({
      id: ind.name,
      name: ind.display_name,
      type: 'indicator',
      category: ind.category,
      data: { 
        indicator: ind.name, 
        label: ind.display_name, 
        params: ind.params.reduce((acc, p) => ({...acc, [p.name]: p.default}), {}),
        visual_settings: ind.visual_settings || {},
        outputs: ind.outputs || ['value'],
        inputs: ind.inputs || []
      },
      ...getCategoryStyle(ind.category)
    }));

    return [...CUSTOM_UTILITIES, ...indicators];
  }, [allIndicatorMetadata]);


  // Load Strategy for Editing
  useEffect(() => {
    if (!strategyId) return;

    const loadStrategy = async () => {
        setIsLoading(true);
        try {
            const response = await StrategyService.getStrategyById(strategyId);
            
            let versionData = null;
            if (versionId) {
                try {
                    const vRes = await StrategyService.getStrategyVersionById(strategyId, versionId);
                    if (vRes.data && (vRes.data.status === 'success' || vRes.data.version_id)) {
                        versionData = vRes.data.data || vRes.data;
                    }
                } catch (err) {
                    console.error('Failed to load specific version, falling back to latest:', err);
                    showSnackbar('Failed to load specific version, using latest', 'warning');
                }
            }

            if (response.data && (response.data.status === 'success' || response.data.strategy_id)) {
                // Handling wrapped/unwrapped responses
                const strategy = response.data.data || response.data;
                
                //--> Always ensure currentParentVersionId is set from loaded strategy/version
                setCurrentParentVersionId(
                    versionData ? (versionData.version_id || versionId) : (strategy.current_version_id || null)
                );
                
                // Use version config if loaded, otherwise strategy config
                let configToUse = versionData ? versionData.config : strategy.config;
                let versionNumToUse = versionData ? versionData.version_number : strategy.version_number;

                // Safe Parsing of Config
                let config = {};
                try {
                    config = typeof configToUse === 'string' 
                        ? JSON.parse(configToUse) 
                        : (configToUse || {});
                } catch (e) {
                    console.error('Failed to parse config:', e);
                    config = {};
                }

                const generalConfig = config.general || {};

                // 1. Set General Info (Safe Defaults)
                setStrategyInfo({
                    name: strategy.name || '',
                    description: strategy.description || '',
                    status: strategy.status || 'draft',
                    executionType: generalConfig.executionType || 'AUTOMATED',
                    version: versionNumToUse || 'v1.0',
                    dateRange: generalConfig.dateRange || { start: null, end: null },
                    executionConfig: {
                      brokerAccounts: generalConfig.brokerAccounts || [],
                      //* config.orders is the per-account order settings authority. It must be restored on
                      //  load: every save writes executionConfig.orders back verbatim, so a load that
                      //  dropped it made the next save persist `orders: {}` and deploy fail with
                      //  "has no per-account order settings".
                      orders: config.orders || {}
                    }
                });

                // 1.1 Set Active Chart Type (Persistence)
                if (generalConfig.activeChartType) {
                    setActiveChartType(generalConfig.activeChartType);
                }

                // 1.2 Set Active Step from saved state (Persistence), overridden by URL
                if (urlStep) {
                    setActiveStep(urlStep);
                } else if (generalConfig.activeStep) {
                    setActiveStep(generalConfig.activeStep);
                }

                // 1.3 Trigger Run Load if coming from History
                if (urlRunId) {
                   setActiveStep('logic');
                   setLogicRightTab('backtest');
                   setLogicViewMode('rd');
                   setActiveRunId(urlRunId);
                   const loadPastRun = async () => {
                       try {
                          const resultResponse = await services.backtesting.getResults(urlRunId);
                          const data = resultResponse.data?.data || resultResponse.data;
                          if (data) {
                              const runStart = data.start_date ? String(data.start_date).split('T')[0] : null;
                              const runEnd = data.end_date ? String(data.end_date).split('T')[0] : new Date().toISOString().split('T')[0];
                              if (runStart) {
                                setStrategyInfo(prev => ({
                                  ...prev,
                                  dateRange: { start: runStart, end: runEnd || runStart }
                                }));
                              }

                              setBacktestConfig(prev => ({
                                  ...prev,
                                  startDate: runStart || prev.startDate,
                                  endDate: runEnd || prev.endDate,
                                  initialCapital: data.initial_capital ?? prev.initialCapital,
                                  slippage: data.slippage_pct ?? prev.slippage,
                              }));
                              if (data.metrics || data.trades) {
                                  const normalizedTrades = normalizeTrades(data.trades);
                                  setBacktestResults({
                                      runId: data.run_id,
                                      metrics: data.metrics,
                                      total_return: data.metrics?.total_return ?? data.total_return,
                                      total_return_pct: data.metrics?.total_return_pct ?? data.total_return_pct,
                                      initial_capital: data.initial_capital,
                                      trades: normalizedTrades,
                                      chartData: data.equity_curve || [],
                                      buyHoldCurve: data.buy_hold_curve || [],
                                  });
                                      setIsBacktestStale(false);
                                  setBacktestSelectedTimeRange(computeSafeZoomWindow(normalizedTrades, data.start_date, data.end_date, exchangeTimezone));
                              }
                              //--> Set dedup ref so late WS/polling for this run is suppressed
                              resultsLoadedForRunRef.current = data.run_id || urlRunId;
                          }
                       } catch (err) {
                           console.error("Failed to load past backtest", err);
                       }
                   };
                   loadPastRun();
                }

                // 2. Set Instrument (Robust Check)
                if (Array.isArray(config.instruments) && config.instruments.length > 0) {
                    console.log('📊 Loading instrument from array:', config.instruments[0]);
                    setSelectedInstrument(config.instruments[0]);
                } else if (config.instrument) {
                    console.log('📊 Loading instrument from single field:', config.instrument);
                    setSelectedInstrument(config.instrument);
                } else {
                    console.warn('⚠️ No instrument found in strategy config');
                }

                // 3. Set Instrument Config
                console.log('⚙️ Instrument Config:', {
                    isContinuous: generalConfig.isContinuous,
                    rolloverDays: generalConfig.rolloverDays
                });
                setInstrumentConfig({
                    isContinuous: Boolean(generalConfig.isContinuous),
                    rolloverDays: Number(generalConfig.rolloverDays) || 2,
                    //! R4 Hop-1 (money-path #30) — default ON (close-out protects capital
                    //  by default); ??-coalesce so an absent/undefined persisted value
                    //  does not read as OFF.
                    autoCloseOut: generalConfig.autoCloseOut ?? true,
                    //! #140/#141 E5 — round-trip the persisted override verbatim; absent key
                    //  reads back as "" (platform default), never a numeric copy of the default.
                    squareOffTime: generalConfig.squareOffTime ?? "",
                    squareOffLeadMinutes: generalConfig.squareOffLeadMinutes !== undefined && generalConfig.squareOffLeadMinutes !== null
                        ? String(generalConfig.squareOffLeadMinutes)
                        : ""
                });

                // 4. Set Logic Graph (Strict Sanitization)
                let normalizedNodes = initialLogicNodes;
                if (Array.isArray(config.nodes)) {
                  const saneNodes = config.nodes
                        .filter(n => n && typeof n === 'object')
                        .map((n, idx) => ({
                            id: n.id || `node_${idx}_${Date.now()}`,
                            type: n.type || 'default',
                            position: n.position || { x: 0, y: 0 },
                            data: n.data || {},
                            ...n 
                        }));

                  // Normalize indicator timeframe from connected data_source.
                  // Root-cause guard: persisted configs can contain stale
                  // indicator params.timeframe that no longer matches sourceId.
                  const saneEdgesRaw = Array.isArray(config.edges)
                    ? config.edges.filter(e => e && e.source && e.target)
                    : [];
                  const saneEdges = sanitizeIncomingEdges(saneEdgesRaw, saneNodes);
                  const nodeMap = new Map(saneNodes.map(n => [n.id, n]));

                  //--> Strip legacy action-agnostic trade_markers default baked into
                  //    signal/position_trigger nodes before 2026-04-11. Older strategies
                  //    persist a generic enter_long-shaped blob that shadows the per-action
                  //    defaults in TRADE_MARKER_DEFAULTS. Detect that exact shape and remove
                  //    it so the builder falls back to action-correct defaults at render.
                  const LEGACY_TRADE_MARKER_DEFAULT = {
                      enabled: true, shape: 'arrowUp', color: '#10B981',
                      position: 'belowBar', autoFlipPosition: false, size: 2,
                  };
                  const matchesLegacyDefault = (tm) => {
                      if (!tm || typeof tm !== 'object') return false;
                      for (const k of Object.keys(LEGACY_TRADE_MARKER_DEFAULT)) {
                          if (tm[k] !== LEGACY_TRADE_MARKER_DEFAULT[k]) return false;
                      }
                      // label sub-object must also match the legacy shape
                      if (!tm.label || tm.label.enabled !== true || tm.label.mode !== 'action_price') return false;
                      // real user customizations add stageOverrides — preserve those
                      if (tm.stageOverrides && Object.keys(tm.stageOverrides).length > 0) return false;
                      return true;
                  };
                  const strippedNodes = saneNodes.map(node => {
                      if (node.type !== 'signal' && node.type !== 'position_trigger') return node;
                      const tm = node.data?.visual_settings?.trade_markers;
                      if (!matchesLegacyDefault(tm)) return node;
                      const { trade_markers: _, ...restVisual } = node.data.visual_settings || {};
                      return { ...node, data: { ...node.data, visual_settings: restVisual } };
                  });

                  normalizedNodes = strippedNodes.map(node => {
                    if (node.type !== 'indicator') return node;

                    const incomingEdges = saneEdges.filter(e => e.target === node.id);
                    const primaryEdge = incomingEdges.find(e => isPrimaryInputHandle(e.targetHandle)) || incomingEdges[0];
                    const srcId = primaryEdge?.source || node.data?.sourceId;
                    const srcNode = srcId ? nodeMap.get(srcId) : null;
                    if (!srcNode || srcNode.type !== 'data_source') return node;

                    const srcTf = srcNode.data?.timeframe;
                    if (!srcTf) return node;

                    const nextData = {
                      ...node.data,
                      sourceId: srcId,
                      timeframe: srcTf,
                      params: {
                        ...(node.data?.params || {}),
                        timeframe: srcTf,
                      },
                    };

                    return { ...node, data: nextData };
                  });

                  setNodesUnsafe(normalizedNodes);
                } else {
                    setNodesUnsafe(initialLogicNodes); // Fallback to avoid empty/undefined state
                }

                if (Array.isArray(config.edges)) {
                  const saneEdges = sanitizeIncomingEdges(
                    config.edges.filter(e => e && e.source && e.target),
                    normalizedNodes
                  );
                    setEdgesUnsafe(saneEdges);
                    
                    // --> DIAGNOSTIC: Compare Edges vs Node Data (Development Only)
                    if (process.env.NODE_ENV === 'development') {
                        console.group('🔍 Strategy Graph Integrity Check');
                        console.log('Total Edges:', saneEdges.length);
                        console.table(saneEdges.map(e => ({ 
                            id: e.id, 
                            source: e.source, 
                            target: e.target 
                        })));
                        
                        // Check each indicator node
                        const indicators = (Array.isArray(config.nodes) ? config.nodes : [])
                            .filter(n => n?.type === 'indicator');
                        
                        console.log('\n📊 Indicator Nodes Data vs Edges:');
                        indicators.forEach(node => {
                            const incomingEdges = saneEdges.filter(e => e.target === node.id);
                            const edgeSources = incomingEdges.map(e => e.source).join(', ') || 'NONE';
                            const nodeDataSource = node.data?.sourceId || 'NONE';
                            const match = (incomingEdges.length === 1 && incomingEdges[0].source === nodeDataSource) || 
                                         (incomingEdges.length === 0 && !nodeDataSource);
                            
                            console.log(`${match ? '✅' : '❌'} ${node.data?.label || node.id}:`, {
                                'Node Data sourceId': nodeDataSource,
                                'Actual Edge Source': edgeSources,
                                'Match': match
                            });
                        });
                        console.groupEnd();
                    }
                } else {
                    setEdgesUnsafe(initialLogicEdges);
                }
                
                console.log('✅ Strategy Loaded:', strategy.name);
                
                // If auto-navigation is not needed, we let 1.2 handle it. 
                // But just in case, only navigate to 'logic' if no saved activeStep exists:
                if (!config.general?.activeStep) {
                    setActiveStep('logic');
                }

                //--> Draft strategies: release chart immediately since there's no backtest to check.
                //    For backtested strategies, the auto-load effect handles this after loading results.
                if (strategy.status === 'draft' || !strategy.status) {
                    setIsBacktestCheckDone(true);
                }
            } else {
                showSnackbar('Strategy data invalid', 'error');
            }
        } catch (error) {
            console.error('Failed to load strategy:', error);
            showSnackbar('Error loading strategy', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    loadStrategy();
  }, [strategyId, versionId, showSnackbar, setNodesUnsafe, setEdgesUnsafe]);
  

  


  //--> Reconcile indicator outputs from backend metadata.
  //--> Saved strategy configs may contain stale outputs lists (e.g. removed outputs).
  React.useEffect(() => {
    if (!allIndicatorMetadata || allIndicatorMetadata.length === 0 || isLoading) return;
    setNodes(prev => reconcileIndicatorOutputs(prev, allIndicatorMetadata));
  }, [allIndicatorMetadata, isLoading, setNodes]);

  // Initialize WebSocket for Data Availability Updates
  React.useEffect(() => {
    // Read token from storage when authenticated
    const storedToken = localStorage.getItem('accessToken');
    
    if (isAuthenticated && storedToken) {
      console.log('🔌 StrategyBuilder: Auth confirmed, initializing WebSocket...');
      WebSocketManager.setAuthToken(storedToken);
      
      const onAuthSuccess = () => console.log('✅ StrategyBuilder: WS Authenticated');
      const onAuthError = (err) => console.error('❌ StrategyBuilder: WS Auth Error', err);
      
      WebSocketManager.on('auth_success', onAuthSuccess);
      WebSocketManager.on('auth_error', onAuthError);

      WebSocketManager.connect()
        .then(() => console.log('✅ StrategyBuilder: WS Connect called'))
        .catch(err => console.error('❌ StrategyBuilder: WS Connect Error', err));
        
      return () => {
          WebSocketManager.off('auth_success', onAuthSuccess);
          WebSocketManager.off('auth_error', onAuthError);
      };
    } else {
        console.log('🔌 StrategyBuilder: Waiting for auth...', { isAuthenticated, hasToken: !!storedToken });
    }
  }, [isAuthenticated]);
  
  // Strategy State
  const [strategyInfo, setStrategyInfo] = useState({
    name: '',
    description: '',
    status: 'draft',
    executionType: 'AUTOMATED',
    dateRange: { start: null, end: null },
    executionConfig: {
      brokerAccounts: []
    }
  });

  //--> Deployment state (Step 4)
  const [deploymentState, setDeploymentState] = useState({
    selectedMode: 'paper',
    activeDeployment: null,
    hasOpenPositions: false,
    isLoading: false,
    isDeploying: false,
    isStopping: false,
  });

  // Dynamic Chart Configuration (Default: Single 1m Chart)
  // This will be updated as user adds logic nodes (timeframes, indicators)
  const [chartConfig, setChartConfig] = useState([
      {
          timeframe: '1m',
          // mode removed to allow override by parent prop
          heightClass: 'h-full',
          showToolbar: true
          // instrument, dateRange will be injected/merged during render based on current selection
      }
  ]);
  
  // State for Throttling
  const lastUpdateRef = useRef(0);

  // Subscribe to Live Ticks
  React.useEffect(() => {
    if (!selectedInstrument || !isAuthenticated) return;
    
    // Skip live subscription if we are looking at a fixed historical range (Backtest/R&D)
    if (strategyInfo.dateRange?.start || strategyInfo.dateRange?.end) {
        return;
    }
    
    const token = selectedInstrument.instrument_token || selectedInstrument.instrument_id;
    if (!token) return;

    const topic = `md:ticks:${token}`;
    console.log('[StrategyBuilder] Subscribing to', topic);
    
    const handleTick = (data) => {
        // Throttle the UI state updates for dashboard
        const now = Date.now();
        if (now - lastUpdateRef.current > 500) {
            setLiveTick(data);
            setLatestData(data);
            lastUpdateRef.current = now;
        }
    };

    const unsubscribe = WebSocketManager.subscribeTopic(topic, handleTick);

    return () => {
        unsubscribe();
    };
  }, [selectedInstrument?.instrument_token, selectedInstrument?.instrument_id, isAuthenticated]);

  //! #18 (R1.1/R1.2 broker-qty-semantics) — the OrderCard order-size clarity needs lot_size, but
  //  the instrument picker's object is a partial (no lot_size). Mirror SmartOrderForm
  //  (frontend/src/components/SmartTrade/SmartOrderForm.jsx:144-158): once an instrument is
  //  selected, ALWAYS refetch its full record from instrument-master and overwrite lot_size /
  //  contract_multiplier — unconditional, not "fetch once and trust whatever's cached", since a
  //  stale/partial persisted config can carry a wrong or missing lot_size that must not survive.
  //  R1.2: agent/MCP-authored configs can hydrate selectedInstrument.instrument_id as an
  //  EXCHANGE:SYMBOL composite key (e.g. "NSE:CRUDEOIL...FUT") instead of a UUID — GET
  //  /instruments/{id} is a strict PK match and 404s on that shape, so branch on the ":" to route
  //  composite keys through the bulk endpoint instead.
  useEffect(() => {
    const id = selectedInstrument?.instrument_id;
    if (!id) return;
    let cancelled = false;
    const isCompositeKey = id.includes(':');

    //! iter-7 — CONTINUOUS-MODE auto-roll. The confirmed defect: a continuous F&O strategy whose
    //! persisted contract is EXPIRED (e.g. CRUDEOIL MAR25, lot=1) showed the expired lot because this
    //! effect had NO continuous branch and its deps never read isContinuous, so toggling continuous
    //! did nothing. The authoritative readiness gate DOES roll for continuous
    //! (deployment_service.resolve_pre_readiness_inputs :298-330): when
    //! `is_continuous AND instrument_type ∈ {FUTURE, OPTION} AND symbol AND exchange` it resolves the
    //! ACTIVE contract via instrument-master GET /instruments/active-contract. We MIRROR that branch
    //! here, reading the SAME selectedInstrument fields the gate reads (buildReadinessConfig sends
    //! `[selectedInstrument]` verbatim, so the gate's `first_instrument` IS this object) and calling
    //! the SAME endpoint with the SAME params — so the displayed lot == the gate-resolved lot BY
    //! CONSTRUCTION (SME rec C.1; drift-proof). Rollable set is the gate's tight {FUTURE, OPTION}, NOT
    //! DERIVATIVE_INSTRUMENT_TYPES — rolling on a type the gate does not roll on would re-diverge.
    const isContinuous = Boolean(instrumentConfig?.isContinuous);
    const rolloverDays = Number(instrumentConfig?.rolloverDays) || 0;
    const baseSymbol = String(selectedInstrument?.symbol || '');
    const exchange = String(selectedInstrument?.exchange || '');
    const instrumentType = String(selectedInstrument?.instrument_type || '').toUpperCase();
    const isRollableContinuous =
      isContinuous && (instrumentType === 'FUTURE' || instrumentType === 'OPTION') && baseSymbol && exchange;

    //--> Merge the freshly-resolved metadata from the authoritative instrument-master record back
    //    onto the selected instrument (only if it hasn't changed under us).
    //! iter-6 — also carry the row's instrument_type so the OrderCard's expired/unresolved-F&O
    //! display (TradeExecutionStep) reflects the RESOLVED record's classification, not the partial
    //! picker/persisted object (which can omit instrument_type). An undefined lot_size is left as-is
    //! (R1.1) so the OrderCard falls back to a lots-only render rather than a confident-but-wrong value.
    const applyMetadata = (row) => {
      if (cancelled || !row) return;
      setSelectedInstrument((prev) => {
        if (!prev || prev.instrument_id !== id) return prev;
        const next = { ...prev };
        if (row.instrument_type != null) next.instrument_type = row.instrument_type;
        if (row.lot_size != null) {
          next.lot_size = row.lot_size;
          next.contract_multiplier = row.contract_multiplier;
        }
        return next;
      });
    };

    const resolve = async () => {
      //! iter-7 — continuous derivative: roll to the ACTIVE contract, mirroring the readiness gate.
      if (isRollableContinuous) {
        //! SAME endpoint + params the gate's resolve_active_contract uses
        //! (instrument_validity_resolver.py:196-206). instrumentType is passed explicitly so an
        //! OPTION continuous strategy is resolved as OPTION, matching the gate (the endpoint would
        //! otherwise default to FUTURE and diverge).
        const res = await services.instrumentMaster.getActiveContract(
          baseSymbol, exchange, rolloverDays, instrumentType,
        );
        const contract = res?.data?.success ? res?.data?.contract : null;
        if (contract && contract.lot_size != null) {
          //! Apply the ACTIVE contract's lot so the OrderCard shows the rolled contract — this is
          //! the lot the gate validated (it passes for continuous). lot_size only (the endpoint
          //! returns no contract_multiplier); leave the persisted multiplier untouched.
          setSelectedInstrument((prev) => {
            if (cancelled || !prev || prev.instrument_id !== id) return prev;
            return { ...prev, instrument_type: instrumentType, lot_size: contract.lot_size };
          });
        }
        //! Roll FAILED (no active contract) → the gate fails CLOSED (BLOCKS). We leave the persisted
        //! lot as-is (honest — never fabricate a lot). The OrderCard's expired "reselect" hint is
        //! scoped to NON-continuous, so a continuous strategy shows a plain lots-only fallback here,
        //! never the wrong "reselect the active contract" advice.
        return;
      }

      if (isCompositeKey) {
        const res = await services.instrumentMaster.getInstrumentsBulk([id]);
        const row = Array.isArray(res?.data) ? res.data[0] : null;
        applyMetadata(row);
        return;
      }

      const res = await services.instrumentMaster.getInstrument(id);
      const row = res?.data;
      if (!row) return;

      //! iter-7 scope note: this strict-PK block is the NON-continuous (pinned) path — reached only
      //! after the isRollableContinuous branch above has returned. For a PINNED contract the gate does
      //! NOT roll, so display must show the persisted row's real lot (below); the continuous roll is
      //! handled above where the gate DOES roll.
      //! broker-qty-semantics loop-back iter-6 — the OrderCard lot display MUST reflect the SAME
      //! instrument/lot the authoritative readiness gate + order-sizing path use, never a display-only
      //! re-resolution to a DIFFERENT contract. get_by_id (GET /instruments/{id}) is the strict-PK
      //! getter the readiness gate itself resolves against (resolve_instrument_validity → the
      //! /instruments/bulk UUID branch → the exact persisted row), so we show the ACTUAL lot_size of
      //! the selected/persisted row here.
      //!
      //! HISTORY (removed here): iter-4/iter-5 added a UUID-path stub→active-contract redirect that,
      //! for a DERIVATIVE resolving to a non-tradable/historic stub (e.g. CRUDEOIL MAR25, expired,
      //! lot=1), re-resolved to the ACTIVE contract's lot (100) FOR DISPLAY. That diverged from the
      //! authoritative gate — which correctly reports the persisted expired contract's lot as
      //! unresolved/expired — so the card showed a confident "100" while the gate blocked. The
      //! divergence confused users on expired contracts. The redirect is removed: an expired/
      //! unresolved F&O now surfaces its REAL lot honestly, and the OrderCard renders an
      //! expired/unresolved state (TradeExecutionStep.jsx) that AGREES with the gate. R1.1/R1.2's
      //! core is preserved — the refetch still overwrites a stale/partial persisted lot_size with the
      //! instrument's real value; only the redirect-to-a-different-contract is gone. The composite-key
      //! (agent/MCP-authored) branch above is UNCHANGED: the P0-1-hardened backend resolver returns
      //! the active tradable contract for that shape, which IS R1.2 by design and agrees with the gate.
      applyMetadata(row);
    };

    resolve().catch(() => { /* non-fatal — the OrderCard falls back to showing lots only */ });
    return () => { cancelled = true; };
    //! iter-7 — isContinuous + rolloverDays ADDED to deps: toggling continuous (the reported "nothing
    //! changed" bug) now re-runs this effect so the card re-resolves (roll ON) or reverts to the
    //! persisted lot (roll OFF). Other selectedInstrument fields are intentionally read but not keyed
    //! (instrument_id is the identity trigger, matching the pre-existing pattern).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstrument?.instrument_id, instrumentConfig?.isContinuous, instrumentConfig?.rolloverDays]);

  // Strategy Execution State (Real-Time from Backend)
  //! indicator-docs-panel R4 — raw node_results snapshot of the LATEST execution message, REPLACED
  //  per message (never merged). `executionData` (flat view) derives from it.
  const [liveNodeResults, setLiveNodeResults] = useState(EMPTY_LIVE_NODE_RESULTS);
  const executionData = useMemo(() => {
    // Flatten node_results to simple { nodeId: value } map for Dashboard
    const flatData = {};
    Object.entries(liveNodeResults || {}).forEach(([nodeId, res]) => {
      if (res && typeof res.value !== 'undefined') flatData[nodeId] = res.value;
    });
    return flatData;
  }, [liveNodeResults]);
  //* 50-17: the wire's invalid_reason per node, so the dashboard condition tree shows a withheld input as unknown.
  const invalidReasonsRef = useRef({});
  const liveNodeInvalidReasons = useMemo(() => {
    invalidReasonsRef.current = keepIfSameReasons(invalidReasonsRef.current, deriveLiveNodeInvalidReasons(liveNodeResults));
    return invalidReasonsRef.current;
  }, [liveNodeResults]);
  const [indicatorMaps, setIndicatorMaps] = useState({});

  //--> Resolve the active deployment when the strategy loads and when entering Step 4
  //    (indicator-docs-panel R4: the Step-3 properties panel needs a deployment id too).
  const latestStrategyIdRef = useRef(strategyId);
  const deploymentRequestSeqRef = useRef(0);
  latestStrategyIdRef.current = strategyId;
  const resolvedForStrategyRef = useRef(null);

  //! Clear the previous strategy's deployment immediately on strategyId change.
  useEffect(() => {
    resolvedForStrategyRef.current = null;
    setDeploymentState(prev => (prev.activeDeployment ? { ...prev, activeDeployment: null, hasOpenPositions: false } : prev));
  }, [strategyId]);

  useEffect(() => {
    if (!strategyId) return;
    if (activeStep !== 'execution' && resolvedForStrategyRef.current === strategyId) return;
    const requestedFor = strategyId;
    //! monotonic request id: a late response from an OLDER request for the SAME strategy
    //! (load / Step-4 entry / deploy refresh overlapping) must not overwrite a newer one
    const requestId = ++deploymentRequestSeqRef.current;

    const fetchDeployment = async () => {
      setDeploymentState(prev => ({ ...prev, isLoading: true }));
      try {
        const res = await StrategyService.getActiveDeployment(requestedFor);
        //! request token: ignore a late response for a previous strategyId or an older request
        if (latestStrategyIdRef.current !== requestedFor) return;
        if (deploymentRequestSeqRef.current !== requestId) return;
        const deployment = res.data?.data || null;
        resolvedForStrategyRef.current = requestedFor;

        setDeploymentState(prev => ({
          ...prev,
          activeDeployment: deployment,
          hasOpenPositions: false,
          isLoading: false,
        }));
      } catch (err) {
        console.error('[StrategyBuilder] Failed to fetch deployment:', err);
        if (latestStrategyIdRef.current !== requestedFor) return;
        if (deploymentRequestSeqRef.current !== requestId) return;
        //! a transient error keeps the previously resolved deployment (it is nulled only on
        //! a strategy change); the live stream must not be torn down by a failed refresh
        setDeploymentState(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchDeployment();
  }, [activeStep, strategyId]);

  //--> R5.6/R4.4.2 — deploy-anyway ack step. `null` = no pending ack confirmation; otherwise
  //    { failingRecommended: ReadinessItem[], snapshotHash, driftResolution }. Populated EITHER
  //    optimistically (client-side, from the current `readinessItems` state, before the network
  //    call — the common/fast path) OR from a server 422 READINESS_ACK_REQUIRED body (N-2 — the
  //    server is the sole source of truth; a stale/absent client-side ack always gets corrected
  //    from what the SERVER just computed, never from an earlier reactive-panel fetch).
  const [ackConfirm, setAckConfirm] = useState(null);

  //--> R6.2 drift prompt: owned HERE, set only by submitDeploy on a 409 DEPLOY_DRIFT_UNRESOLVED
  //    ({ hash, divergedAccounts, ackParams } | null). DeploymentSection renders it via
  //    DriftResolutionPanel; the operator's choice re-enters ONLY through resolveDrift.
  const [driftPrompt, setDriftPrompt] = useState(null);

  //--> Deploy handler (single chokepoint: every deploy entry ends here)
  const submitDeploy = async (driftResolution = null, ackParams = null) => {
    if (!strategyId || !currentParentVersionId) {
      showSnackbar('Cannot deploy: strategy or version not loaded. Try saving first.', 'error');
      return;
    }
    setDriftPrompt(null);
    setDeploymentState(prev => ({ ...prev, isDeploying: true }));
    try {
      await StrategyService.deployStrategy(
        strategyId, currentParentVersionId, deploymentState.selectedMode, driftResolution, ackParams,
      );
      showSnackbar(`Strategy deployed as ${deploymentState.selectedMode === 'live' ? 'Live Trade' : 'Paper Trade'}!`, 'success');
      //--> Refresh deployment state
      const res = await StrategyService.getActiveDeployment(strategyId);
      setDeploymentState(prev => ({
        ...prev,
        activeDeployment: res.data?.data || null,
        isDeploying: false,
      }));
      //--> Update local strategy status
      setStrategyInfo(prev => ({ ...prev, status: deploymentState.selectedMode }));
      return;
    } catch (err) {
      //--> R6.2.2 — a 409 DEPLOY_DRIFT_UNRESOLVED opens the drift panel (recoverable, not an error).
      const drift = extractDriftConflict(err);
      if (drift) {
        setDriftPrompt({ hash: drift.diffSnapshotHash, divergedAccounts: drift.divergedAccounts, ackParams });
        setDeploymentState(prev => ({ ...prev, isDeploying: false }));
        return;
      }
      const detail = err?.response?.data?.detail;
      const code = detail?.error?.code;
      //! R4.4.2/N-2 — server disagrees (no ack, stale ack, or content changed since ack) — the
      //  server is the SOLE authority; discard any client-side ack state and re-open the confirm
      //  step populated from THIS response body (error.details.readiness /
      //  error.details.readiness_snapshot_hash), never from an earlier reactive fetch.
      if (err?.response?.status === 422 && code === 'READINESS_ACK_REQUIRED') {
        const readiness = detail?.error?.details?.readiness || [];
        const snapshotHash = detail?.error?.details?.readiness_snapshot_hash || null;
        setAckConfirm({
          failingRecommended: readiness.filter((i) => i.severity === 'RECOMMENDED' && i.pass === false),
          snapshotHash,
          driftResolution,
        });
        setDeploymentState(prev => ({ ...prev, isDeploying: false }));
        return;
      }
      //! R4.1/R4.3 — hard block, no ack path. Should be unreachable via the FE (the Deploy button
      //  is disabled per R5.4 when any REQUIRED item fails) but the server enforces regardless of
      //  a direct/agent API caller (feedback_agent_authoring_equals_fe_user) — surface it plainly.
      if (err?.response?.status === 422 && code === 'READINESS_REQUIRED_FAILED') {
        const failingLabels = (detail?.error?.details?.readiness || [])
          .filter((i) => i.severity === 'REQUIRED' && i.pass === false)
          .map((i) => i.label)
          .join(', ');
        showSnackbar(
          `Cannot deploy — required readiness item(s) failing: ${failingLabels || 'see checklist'}`,
          'error',
        );
        setDeploymentState(prev => ({ ...prev, isDeploying: false }));
        return;
      }
      const msg = (typeof detail === 'string' ? detail : detail?.error?.message) || err.message || 'Deploy failed';
      showSnackbar(msg, 'error');
      setDeploymentState(prev => ({ ...prev, isDeploying: false }));
      return;
    }
  };

  const handleDeploy = async () => {
    //--> R5.6 — optimistic client-side pre-check (fast path): if LIVE + all REQUIRED pass + any
    //    RECOMMENDED failing, open the "deploy anyway" confirm step BEFORE sending the request at
    //    all (its own AC: "SHALL NOT submit the deploy request until the user confirms it"). If the
    //    client's readinessItems turn out stale, submitDeploy's 422 handler above re-opens this
    //    same confirm step from the server's fresh response instead.
    const anyRequiredFailing = (readinessItems || []).some((i) => i.severity === 'REQUIRED' && i.pass === false);
    const failingRecommended = (readinessItems || []).filter((i) => i.severity === 'RECOMMENDED' && i.pass === false);
    if (deploymentState.selectedMode === 'live' && !anyRequiredFailing && failingRecommended.length > 0) {
      setAckConfirm({ failingRecommended, snapshotHash: readinessSnapshotHash, driftResolution: null });
      return;
    }
    return submitDeploy(null, null);
  };

  //--> Drift panel action: resubmit through the one chokepoint with the 409's hash (OCC retry).
  const resolveDrift = async (resolution) => {
    if (!driftPrompt) return;
    const { hash, ackParams } = driftPrompt;
    //--> submitDeploy is the ONE place that clears driftPrompt (after its not-loaded guard), so an
    //    early return keeps the panel open instead of dropping it behind an error toast (Verify F5).
    return submitDeploy({ resolution, diff_snapshot_hash: hash }, ackParams);
  };
  const dismissDrift = () => setDriftPrompt(null);

  //--> Confirms the R5.6 "deploy anyway" step — resubmits the SAME deployStrategy call, now
  //    carrying ack:true + the acknowledged item ids + the snapshot hash (client-optimistic or
  //    server-fresh, per whichever populated `ackConfirm`).
  const confirmAckAndDeploy = async () => {
    if (!ackConfirm) return;
    const { failingRecommended, snapshotHash, driftResolution } = ackConfirm;
    setAckConfirm(null);
    return submitDeploy(driftResolution, {
      ack: true,
      acknowledged_item_ids: failingRecommended.map((i) => i.id),
      acknowledged_snapshot_hash: snapshotHash,
    });
  };

  //--> Stop handler — checks open positions before stopping
  const handleStopDeployment = async () => {
    const deployment = deploymentState.activeDeployment;
    if (!deployment || !strategyId) return;
    setDeploymentState(prev => ({ ...prev, isStopping: true }));
    try {
      //--> Check OMS for open positions before stopping
      try {
        //! R5.8/D2 (money-path #30) — key the stop-gate SII on the RESOLVED instrument,
        //  not the frozen builder id. Post-roll, OMS/worker key strategy_instance_id on
        //  the resolved (subscribed/traded) instrument (instance_manager.py:983-991,
        //  3311-3318); a frozen-id lookup 404s -> falls into the catch below -> falsely
        //  "safe to stop" while a real open position exists on the resolved contract.
        //  Depends on R5.2b backend enrichment of get_active(); resolved_instrument_id
        //  is undefined until that lands, in which case this is a no-op fallback to the
        //  frozen id (same behavior as before).
        const instanceId = `${user?.id}:${strategyId}:${deployment.version_id}:${deployment.resolved_instrument_id ?? deployment.instrument_id}:${deployment.timeframe}`;
        const posRes = await StrategyService.getPositionForVersion(instanceId);
        const pos = posRes.data?.data;
        if (pos && pos.side !== 'FLAT') {
          setDeploymentState(prev => ({ ...prev, hasOpenPositions: true, isStopping: false }));
          showSnackbar('Close open positions before stopping deployment.', 'warning');
          return;
        }
      } catch {
        //--> 404 or error means no position record — safe to stop
      }

      await StrategyService.stopDeployment(strategyId, deployment.deployment_id);
      showSnackbar('Deployment stopped.', 'success');
      setDeploymentState(prev => ({
        ...prev,
        activeDeployment: null,
        hasOpenPositions: false,
        isStopping: false,
      }));
      setStrategyInfo(prev => ({ ...prev, status: 'backtested' }));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Stop failed';
      showSnackbar(msg, 'error');
      setDeploymentState(prev => ({ ...prev, isStopping: false }));
    }
  };

    const handleBacktestRequired = useCallback((meta = {}) => {
      if (!isBacktestStale) {
        setIsBacktestStale(true);
        const reason = meta?.intent === 'backtest_rerun_required'
          ? 'Strategy changed. Please run Backtest (rerun) to refresh indicator details.'
          : 'Strategy changed. Please run Backtest to refresh indicator details.';
        showSnackbar(reason, 'warning');
      }
    }, [isBacktestStale, showSnackbar]);

    const FUNCTIONAL_VALUE_NODE_TYPES = useMemo(() => (
        new Set(['logic_gate', 'logic', 'signal', 'comparator', 'position_trigger'])
    ), []);

  // Subscribe to Strategy Execution Stream (Redis -> WS) — keyed by DEPLOYMENT id (the worker
  // publishes strategy.execution.<deployment_id>, instance_manager.py:2323).
  const activeDeploymentId = deploymentState.activeDeployment?.deployment_id;
  React.useEffect(() => {
      //! deployment change / stop: drop the previous snapshot
      setLiveNodeResults(EMPTY_LIVE_NODE_RESULTS);
      if (!activeDeploymentId || !isAuthenticated) return undefined;

      const topic = `strategy.execution.${activeDeploymentId}`;
      //! generation token: cleanup flips it BEFORE unsubscribing so a late frame can never repopulate state
      let active = true;

      const handleExecutionUpdate = (msg) => {
          if (!active) return;
          if (!msg || msg.deployment_id !== activeDeploymentId) return;
          if (msg.node_results && typeof msg.node_results === 'object') {
              setLiveNodeResults(msg.node_results);
          }
      };

      const unsubscribe = WebSocketManager.subscribeTopic(topic, handleExecutionUpdate);
      return () => {
          active = false;
          unsubscribe();
      };
  }, [activeDeploymentId, isAuthenticated]);

  const [logicViewMode, setLogicViewMode] = useState('flow'); // 'flow' (Canvas) | 'rd'
  const {
    subView: canvasSubView,
    outlineOpen,
    outlineWidth,
    labelMode,
    setSubView: setCanvasSubView,
    setOutlineOpen,
    setOutlineWidth,
    setLabelMode,
  } = useCanvasViewPrefs();
  const [canvasFocusRequest, setCanvasFocusRequest] = useState(0);
  const [logicRightTab, setLogicRightTab] = useState('builder'); // 'builder' | 'backtest'

  // Stabilize visualLayers with a refined memoization
  // 1. Prepare visual layers for SmartChart (Strictly Filtered)
  const visualLayers = useMemo(() => {
    if (isLoading || !Array.isArray(nodes) || nodes.length === 0) return [];
    return deriveVisualLayers(effectiveNodes, edges);
  }, [nodes, effectiveNodes, edges, isLoading]);
  const activeTimeframe = useMemo(() => {
    if (isLoading || !Array.isArray(nodes)) return '5m';
    return nodes.find(n => n?.type === 'data_source')?.data?.timeframe || '5m';
  }, [nodes, isLoading]);
  const modelVersionScope = useMemo(() => ({
    instrumentId: selectedInstrument?.instrument_id,
    instrumentLabel: selectedInstrument?.symbol || selectedInstrument?.name,
    timeframe: activeTimeframe,
  }), [selectedInstrument?.instrument_id, selectedInstrument?.symbol, selectedInstrument?.name, activeTimeframe]);
  const authorizedToolboxIndicators = useMemo(
    () => filterToolboxIndicatorsByRole(toolboxIndicators, isSuperuser),
    [toolboxIndicators, isSuperuser],
  );
  const canvasNodes = useMemo(
    () => lockModelNodesForRole(nodes, isSuperuser),
    [nodes, isSuperuser],
  );

  // Memoize charts prop to prevent re-renders on hover/tick
  const chartsConfig = useMemo(() => {
    if (isLoading) return [];
    return [{
      timeframe: activeTimeframe,
      layers: visualLayers.length > 0 ? visualLayers : [{ type: 'candle', styleOptions: {} }],
      heightClass: 'h-full',
      showToolbar: false
    }];
  }, [activeTimeframe, visualLayers, isLoading]);

  // ─── Effective date range: period selection overrides full strategy range ───
  // When user selects a period from BacktestPeriodAnalysis, SmartChart loads
  // OHLC data for just that period from the backend (on-demand).
  const effectiveDateRange = useMemo(() => {
    let result;
    if (backtestSelectedTimeRange) {
      result = backtestSelectedTimeRange;
    } else if ((backtestResults?.runId || urlRunId) && strategyInfo.dateRange?.start && !strategyInfo.dateRange?.end) {
      result = { start: strategyInfo.dateRange.start, end: new Date().toISOString().split('T')[0] };
    } else {
      result = strategyInfo.dateRange;
    }
    return result;
  }, [backtestSelectedTimeRange, strategyInfo.dateRange, backtestResults?.runId, urlRunId]);

  //--> fetchDateRange: Use backtest run dates when viewing results, otherwise strategy dates.
  //    IMPORTANT: Do NOT depend on logicRightTab — switching Builder/Backtest tabs must NOT
  //    trigger a chart data re-fetch. Only actual backtest results (runId) should switch ranges.
  //    This must always be the FULL backtest range so all pages are available for any zoom target.
  //    Period selections only affect dateRange (zoom), not fetchDateRange (fetch).
  const memoizedFetchDateRange = useMemo(() => {
      //--> Also use backtestConfig when a stale run was detected but we still want to
      //    anchor the chart to that run's date range (auto-load sets dates, not results).
      if (backtestResults?.runId || urlRunId || isBacktestStale) {
          const start = backtestConfig.startDate || strategyInfo.dateRange?.start;
          const end = backtestConfig.endDate || strategyInfo.dateRange?.end || new Date().toISOString().split('T')[0];
          return { start, end };
      }
      return strategyInfo.dateRange;
  }, [backtestResults?.runId, urlRunId, isBacktestStale, backtestConfig.startDate, backtestConfig.endDate, strategyInfo.dateRange]);

  const memoizedLayers = useMemo(() => {
     return (visualLayers || []).filter(l => {
          if (l.styleOptions?.is_overlay || l.type === 'marker' || l.type === 'custom') return true;
          if (l.type === 'volume') return true;
          const mainSeriesTypes = ['candle', 'heikin', 'hollow_candle', 'line', 'area'];
          if (mainSeriesTypes.includes(l.type)) return l.type === activeChartType;
          return true;
     });
  }, [visualLayers, activeChartType]);

  const handleDataLoaded = useCallback((sampleData) => {
      if (sampleData && Object.keys(sampleData).length > 0) {
          setDataSchema(sampleData);
      }
  }, []);

  // 2. Prepare strategy indicators with Backend Protocol Enforcement
  const strategyIndicators = useMemo(() => {
    if (isLoading || !Array.isArray(nodes) || nodes.length === 0) return [];
    //! 50-8 D35 — display fields (colour, visibility, style) come from the EFFECTIVE nodes; the
    //! computation fields (indicator, params, sourceId, inputs) are not VISUAL_DATA_KEYS members, so
    //! they are identical to the raw version nodes by construction.
    const indicators = deriveStrategyIndicators(effectiveNodes, edges, allIndicatorMetadata, resolveSmartLabel);
    return indicators.sort((a, b) => (a.isHidden ? 1 : 0) - (b.isHidden ? 1 : 0));
  }, [nodes, effectiveNodes, edges, allIndicatorMetadata, isLoading]);

  // Chart Indicators State (R&D Mode)
  const [activeIndicators, setActiveIndicators] = useState([]);

  // Configurable Chart Types State
  const [visibleChartTypes, setVisibleChartTypes] = useState(['candle', 'line']);
  const [isChartTypeAdderOpen, setIsChartTypeAdderOpen] = useState(false);

  // Available Chart Types Definition
  const ALL_CHART_TYPES = [
      { id: 'candle', label: 'Candlestick', icon: FiBarChart2, description: 'OHLCV bars' },
      { id: 'line', label: 'Line', icon: FiActivity, description: 'Close price' },
      { id: 'heikin', label: 'Heikin Ashi', icon: FiTrendingUp, description: 'Smoothed' },
      { id: 'area', label: 'Area', icon: FiLayers, description: 'Volume profile' }
  ];

  const sanitizeIncomingEdges = useCallback((edgeList = [], nodeList = []) => {
    return sanitizeIncomingEdgesForIndicatorNodes(edgeList, nodeList);
  }, []);

  /*>>> R5.2 — deployment-readiness reactive evaluation state <<<*/
  //  (docs/pipelines/2026-07-31-strategy-deploy-readiness-checklist/02-design.md §4/§6)
  const [readinessItems, setReadinessItems] = useState([]);
  const [readinessSnapshotHash, setReadinessSnapshotHash] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  //! Visual-gate fix — surfaced to ReadinessPanel so a failed reactive eval shows a non-alarming
  //  retry hint instead of silently leaving the panel on stale/empty items (ReadinessPanel.jsx).
  const [readinessError, setReadinessError] = useState(false);

  //--> Builds the CURRENT in-editor graph payload for the reactive readiness call — mirrors
  //    handleSaveStrategy's config shape (nodes/edges/general/instruments/orders) and reuses the
  //    SAME qty-mirroring (mirrorExecutionQtyToData, #18 money-path) + edge-sanitization
  //    (sanitizeIncomingEdges) helpers so the preview inspects the SAME data the deploy path will,
  //    not an approximation — per R5.2's own AC ("the current in-editor graph... including any
  //    unsaved edits", not a refetch of the last-persisted version).
  //  consult-gap fix — the backend can only tell which per-account OrderCard entries in `orders`
  //  are LIVE vs a stale deselected account's preserved-but-hidden entry (R1.1.1,
  //  TradeExecutionStep.jsx:1015-1021 — deselecting NEVER prunes `orders`) by being told the
  //  currently-selected set explicitly. `selectedAccounts` below is sourced from
  //  `strategyInfo.executionConfig?.brokerAccounts`, the EXACT upstream mirror of
  //  TradeExecutionStep's own `selectedAccounts` local state (synced via its
  //  `onConfigChangeRef.current({ brokerAccounts: selectedAccounts })` effect,
  //  TradeExecutionStep.jsx:940-942) — the SAME set `perAccountPlacementTally` iterates
  //  (TradeExecutionStep.jsx:1311-1347) for the on-screen per-account tally. Sending this lets the
  //  backend filter its sizing_balanced check to only the accounts the tally itself considers, so
  //  the checklist can never disagree with what's on screen.
  const buildReadinessConfig = useCallback(() => {
    const qtyMirroredNodes = mirrorExecutionQtyToData(nodes || []);
    return {
      general: {
        executionType: strategyInfo.executionType,
        dateRange: strategyInfo.dateRange,
        isContinuous: instrumentConfig.isContinuous,
        rolloverDays: instrumentConfig.rolloverDays,
        //! #30 S4 (Deepak, 2026-08-02) — MUST carry autoCloseOut into the reactive readiness POST.
        //  The server items `_pinned_no_autoroll`/`_autoclose_off_warning` read
        //  `general.autoCloseOut` (default True) to pick their tone (amber caution vs red warning).
        //  It was omitted here, so the reactive panel ALWAYS saw the default True and never reacted
        //  to the toggle (the persisted deploy config at :2064 already sends it — this is the
        //  reactive-path parity fix). Paired with the effect-dep add below so the toggle re-fires.
        autoCloseOut: instrumentConfig.autoCloseOut,
      },
      instruments: selectedInstrument ? [selectedInstrument] : [],
      nodes: qtyMirroredNodes,
      edges: sanitizeIncomingEdges(edges, qtyMirroredNodes),
      orders: strategyInfo.executionConfig?.orders || {},
      selectedAccounts: strategyInfo.executionConfig?.brokerAccounts || [],
    };
  }, [nodes, edges, strategyInfo, instrumentConfig, selectedInstrument, sanitizeIncomingEdges]);

  //--> Debounced (500ms) reactive re-evaluation, keyed on [nodes, edges, deploymentMode,
  //    executionConfig.orders] per R5.2 — same setTimeout/clearTimeout-in-useEffect idiom already
  //    used for the resize dispatch above (:299-302), not a new debounce utility. No POST fires
  //    before a strategy exists (ad-hoc graphs for a not-yet-created strategy have no strategy_id
  //    to evaluate against — the FIRST save creates it; this mirrors the reactive endpoint's own
  //    ownership-check requirement).
  //! CRITICAL reactivity note (bug: "quantity is not monitored live") — the per-account OrderCard
  //  qty edit does NOT touch `nodes`/`edges` at all; it writes ONLY to
  //  `executionConfig.orders[nodeId][accountId].quantity` via TradeExecutionStep's
  //  `handleOrderChange` -> `onConfigChange` (TradeExecutionStep.jsx:1128-1141), which replaces
  //  `strategyInfo.executionConfig` (and its `orders` map) with a NEW object reference on every
  //  field write (StrategyBuilder.jsx onConfigChange, :3170-3173 — immutable spread, never
  //  mutated in place). Depending on `strategyInfo.executionConfig?.orders` below is therefore a
  //  safe, stable trigger (referential-equality change on every qty edit) — without it, a qty-only
  //  edit left `nodes`/`edges`/`selectedMode`/`strategyId` all unchanged and this effect never
  //  re-fired, so the readiness checklist silently kept evaluating the stale pre-edit quantity.
  //! consult-gap fix — `strategyInfo.executionConfig?.brokerAccounts` (the same set now sent as
  //  `selectedAccounts`) is ALSO added below. Toggling an account's selection (chip click) alone
  //  does NOT touch `strategyInfo.executionConfig.orders` — TradeExecutionStep's `orders` useMemo
  //  only merges DEFAULT_ORDER for a freshly-selected account locally; that merged value is only
  //  pushed upstream via `handleOrderChange` on an actual field edit (TradeExecutionStep.jsx:1128-
  //  1141). Without this dep, selecting/deselecting an account (with no other edit) would leave
  //  this effect unfired and the readiness checklist would keep evaluating the stale account set.
  useEffect(() => {
    if (!strategyId) return undefined;
    const timer = setTimeout(async () => {
      setReadinessLoading(true);
      setReadinessError(false);
      try {
        const config = buildReadinessConfig();
        const res = await StrategyService.evaluateReadiness(strategyId, config, deploymentState.selectedMode);
        const data = res?.data?.data;
        setReadinessItems(Array.isArray(data?.items) ? data.items : []);
        setReadinessSnapshotHash(data?.readiness_snapshot_hash || null);
        setReadinessError(false);
      } catch (err) {
        console.log('[DIAG] readiness reactive evaluation failed', { err: err?.message });
        setReadinessError(true);
      } finally {
        setReadinessLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, deploymentState.selectedMode, strategyId, strategyInfo.executionConfig?.orders, strategyInfo.executionConfig?.brokerAccounts,
      //! #30 S4 (Deepak, 2026-08-02) — toggling autoCloseOut/isContinuous changes ONLY
      //  `instrumentConfig`, none of the deps above, so the readiness POST never re-fired and the
      //  Step-4 panel did not react to the toggle. Adding these makes the toggle debounce-refetch a
      //  fresh readiness result (carrying the new autoCloseOut via buildReadinessConfig).
      instrumentConfig.autoCloseOut, instrumentConfig.isContinuous, instrumentConfig.rolloverDays]);

  //--> R5.3 fix-affordance target — reuses the SAME setter the canvas's own onNodeClick uses
  //    (setSelectedLogicNode, below) and additionally navigates the user to the canvas (Step-4's
  //    Review&Deploy view does not itself render the ReactFlow canvas). The canvas centres only on an
  //    explicit focus request (StrategyLogicStep), so this bumps the same request Locate does.
  const handleReadinessSelectNode = useCallback((node) => {
    if (!node) return;
    setSelectedLogicNode(node);
    setLogicViewMode('flow');
    setActiveStep('logic');
    setCanvasFocusRequest((request) => request + 1);
  }, []);

  // Helper to update node data from Right Pane
  //! 50-8 R1 — display writes (VISUAL_DATA_KEYS members) on a SAVED strategy go to the shared
  //! record's autosave (`visualStore.updateNodeDisplay`) and NEVER touch the raw node; before the
  //! first save (no strategyId) they stay in local node data like every other edit (R5 creation-time
  //! default). A-3: on a SAVED strategy the record is the only home — while it is not writable
  //! (loading / load failed / read-only) the display part of an edit is REFUSED (the store drops it),
  //! never written to node data, and the controls show the disabled state + hint.
  const persistsDisplay = !!strategyId && visualStore.displayWritable;
  const displayLocked = !!strategyId && !visualStore.displayWritable;
  const displayLockHint = displayLockHintFor(strategyId, visualStore); // F-arb-4: also covers the load window
  const updateNodeData = (id, newDataIn) => {
    // --> Intercept Indicator Updates for Auto-Wiring and Graph Sync
    const targetNode = nodes.find(n => n.id === id);
    if (!canEditNodeForRole(targetNode, isSuperuser)) return;
    let newData = newDataIn;
    if (strategyId) {
      const displayPatch = pickVisualMembers(newDataIn);
      if (Object.keys(displayPatch).length > 0) {
        visualStore.updateNodeDisplay(id, displayPatch); // refuses when the record is not writable
        newData = Object.fromEntries(Object.entries(newDataIn).filter(([k]) => !(k in displayPatch)));
        if (Object.keys(newData).length === 0) return;
      }
    }
    let injectedData = { ...newData };
    const currentSourceId = targetNode?.data?.sourceId;
    
    // 1. HANDLE TIMEFRAME AUTO-WIRING (Cascade logic)
    if (targetNode?.type === 'indicator' && newData.timeframe && newData.timeframe !== targetNode.data.timeframe) {
        const currentSource = nodes.find(n => n.id === currentSourceId) || nodes.find(n => n.type === 'data_source');
        const targetLabel = currentSource?.data?.label || 'Market Data';
        
        // Check for existing source with same Label + New Timeframe
        const existingSource = nodes.find(n => 
            n.type === 'data_source' && 
            n.data.timeframe === newData.timeframe &&
            (n.data.label === targetLabel)
        );
        
        if (existingSource) {
            console.log(`[Auto-Wire] Re-linking to existing source: ${existingSource.id}`);
            injectedData.sourceId = existingSource.id;
        } else {
            // Create NEW Secondary Source
            let replacementPosition = null;
            if (currentSource) {
                 const isOldSecondary = currentSource.data?.isSecondary;
                 const remainingUsers = nodes.filter(n => 
                     n.id !== id && 
                     n.type === 'indicator' && 
                     n.data.sourceId === currentSourceId
                 );
                 if (isOldSecondary && remainingUsers.length === 0) {
                     replacementPosition = currentSource.position;
                 }
            }

            console.log(`[Auto-Wire] Creating secondary source for ${newData.timeframe}`);
            const result = createNode('data_source', replacementPosition, { 
                 label: targetLabel, 
                 timeframe: newData.timeframe,
                 isSecondary: true 
            });
            
            if (result.success) {
                injectedData.sourceId = result.node.id;
                showSnackbar(`Created secondary source: ${targetLabel} (${newData.timeframe})`, 'success');
            }
        }
    }

    // 2. EDGE WIRING SYNC (Properties -> Graph)
    // Triggers if sourceId OR sourceHandle was changed manually via dropdown OR automatically via timeframe logic above
    const newSourceId = injectedData.sourceId; 
    const newSourceHandle = injectedData.sourceHandle;
    const currentSourceHandle = targetNode?.data?.sourceHandle;
    
    // Check if Source ID changed OR Handle changed (but only if we have a valid source)
    if ((newSourceId && newSourceId !== currentSourceId) || (newSourceId && newSourceHandle !== currentSourceHandle)) {
      // Keep indicator timeframe in sync with selected data_source.
      if (targetNode?.type === 'indicator' && newSourceId) {
        const selectedSource = nodes.find(n => n.id === newSourceId);
        if (selectedSource?.type === 'data_source' && selectedSource?.data?.timeframe) {
          injectedData.timeframe = selectedSource.data.timeframe;
        }
      }

        console.log(`[Graph-Sync] Re-wiring: ${currentSourceId}:${currentSourceHandle || ''} -> ${newSourceId}:${newSourceHandle || ''} for node ${id}`);
        setEdges(eds => {
          const normalizedTargetHandle = injectedData.targetHandle || 'input_0';
          // Remove ambiguous/duplicate incoming edges before wiring new source.
          const filtered = eds.filter(e => {
            if (e.target !== id) return true;
            if (targetNode?.type === 'indicator') return false;
            return (e.targetHandle || 'input_0') !== normalizedTargetHandle;
          });
            // Add new edge with handle
            const newEdge = {
              id: `e_${newSourceId}_${id}_${normalizedTargetHandle}_${newSourceHandle || 'value'}`,
                source: newSourceId,
                target: id,
                animated: true,
                style: { stroke: 'var(--color-accent)', strokeWidth: 1.5 },
            sourceHandle: newSourceHandle, // Pass specific output handle 
            targetHandle: normalizedTargetHandle
            };
            return [...filtered, newEdge];
        });

        // 3. CLEANUP ORPHANED SECONDARY SOURCES
        const oldSourceNode = nodes.find(n => n.id === currentSourceId);
        if (oldSourceNode?.data?.isSecondary) {
            const remainingUsers = nodes.filter(n => 
                n.id !== id && 
                n.type === 'indicator' && 
                n.data.sourceId === currentSourceId
            );
            
            if (remainingUsers.length === 0) {
                console.log(`[Cleanup] Removing orphaned source: ${oldSourceNode.id}`);
                setNodes(currentNodes => {
                    const survivingNodes = currentNodes.filter(n => n.id !== currentSourceId);
                    const sources = survivingNodes.filter(n => n.type === 'data_source')
                                                  .sort((a, b) => a.position.y - b.position.y);
                    
                    if (sources.length > 0) {
                        const primary = sources.find(n => !n.data.isSecondary) || sources[0];
                        let currentY = primary.position.y;
                        const newPositions = {};
                        
                        sources.forEach(node => {
                            const height = NODE_HEIGHTS[node.type] || 120;
                            newPositions[node.id] = { ...node.position, y: currentY };
                            currentY += height + PADDING_Y;
                        });
                        
                        return survivingNodes.map(n => {
                            if (newPositions[n.id]) {
                                return { ...n, position: newPositions[n.id] };
                            }
                            return n;
                        });
                    }
                    return survivingNodes;
                });

                setEdges(currentEdges => currentEdges.filter(e => e.source !== currentSourceId && e.target !== currentSourceId));
                showSnackbar(`Cleaned up unused source: ${oldSourceNode.data.timeframe}`, 'info');
            }
        }
    }

    setNodes((nds) => {
      // 1. Identify if we are updating a data_source timeframe
      const isDataSourceTimeframeUpdate = targetNode?.type === 'data_source' && newData.timeframe && newData.timeframe !== targetNode.data.timeframe;

      let updatedSelectedNode = null;

      const nextNodes = nds.map((node) => {
        // A. Primary Update: The target node itself
        if (node.id === id) {
          const mergedData = { ...node.data, ...injectedData };
          // Sync params.timeframe when timeframe changes on indicator nodes
          if (node.type === 'indicator' && injectedData.timeframe && mergedData.params) {
            mergedData.params = { ...mergedData.params, timeframe: injectedData.timeframe };
          }
          const updatedNode = { ...node, data: mergedData };
          if (selectedLogicNode?.id === id) {
            updatedSelectedNode = updatedNode;
          }
          return updatedNode;
        }

        // B. Cascade Update: Propagation to dependent indicators
        if (isDataSourceTimeframeUpdate && node.type === 'indicator' && node.data.sourceId === id) {
           console.log(`[Propagation] Updating connected indicator ${node.id} to ${newData.timeframe}`);
           const cascadeData = { ...node.data, timeframe: newData.timeframe };
           // Sync params.timeframe to keep definition cache key consistent
           if (cascadeData.params) {
             cascadeData.params = { ...cascadeData.params, timeframe: newData.timeframe };
           }
           const updatedIndicator = { ...node, data: cascadeData };
           
           // If the indicator is currently selected, update its props view too
           if (selectedLogicNode?.id === node.id) {
               updatedSelectedNode = updatedIndicator;
           }
           return updatedIndicator;
        }

        return node;
      });

      // Update selected node state OUTSIDE the mapping loop to avoid inconsistent intermediate renders
      // Update selected node state synchronously to ensure immediate UI feedback
      if (updatedSelectedNode) {
          setSelectedLogicNode(updatedSelectedNode);
      }

      return nextNodes;
    });
  };

  //--> 50-8 — the context node cards use: ONE `write` (= updateNodeData above, via a ref so the
  //    provider value stays referentially stable) and the record they read effective data from.
  const updateNodeDataRef = useRef(updateNodeData);
  updateNodeDataRef.current = updateNodeData;
  const writeNodeDisplay = useCallback((nodeId, patch) => updateNodeDataRef.current(nodeId, patch), []);
  const visualOverridesContextValue = useMemo(() => ({
    strategyId,
    overridesSaved: visualStore.overridesSaved,
    draftOverrides: visualStore.draftOverrides,
    effectiveOverrides: visualStore.effectiveOverrides,
    write: writeNodeDisplay,
    canWrite: !displayLocked,
    bodyTooLarge: visualStore.bodyTooLarge,
  }), [strategyId, visualStore.overridesSaved, visualStore.draftOverrides,
    visualStore.effectiveOverrides, visualStore.bodyTooLarge, writeNodeDisplay, displayLocked]);

  // --> Sync Functional Node Values to Graph Nodes (Live Status)
  React.useEffect(() => {
      if (!indicatorMaps || Object.keys(indicatorMaps).length === 0) return;

      //! 142-49 (Codex gate consult): streamed values are NOT written into canonical node state —
      //! `nodes[].data.value` feeds readiness POSTs, the chart graph key (/indicators/calculate),
      //! broadcast and Save. The execution stream reaches only the dashboard flat view
      //! (executionData). Calculated badges keep today's path.

      setNodes((nds) => {
          let __changed = false;
          const next = nds.map((node) => {
              if (!FUNCTIONAL_VALUE_NODE_TYPES.has(node.type)) return node;


              let calculatedValue;
              const map = indicatorMaps?.[node.id];
              // Efficiently find the latest value in the Map
              if (map instanceof Map && map.size > 0) {
                  let maxT = -1;
                  map.forEach((v, t) => {
                      if (t > maxT) {
                          maxT = t;
                          calculatedValue = v;
                      }
                  });
              }

              const liveValue = calculatedValue;

              if (liveValue !== undefined && node.data.value !== liveValue) {
                  __changed = true;
                  return { ...node, data: { ...node.data, value: liveValue } };
              }
              return node;
          });
          return __changed ? next : nds;
      });
    }, [indicatorMaps, setNodes, FUNCTIONAL_VALUE_NODE_TYPES]);

  // Sync Instrument Selection to Graph Data Source Node
  React.useEffect(() => {
    if (selectedInstrument) {
        setNodes((nds) => nds.map((node) => {
            if (node.type === 'data_source') {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        label: selectedInstrument.symbol,
                        instrument: selectedInstrument
                    }
                };
            }
            return node;
        }));
    }
  }, [selectedInstrument, setNodes]);

  // --> Sync Selected Node with Nodes Array
  // This ensures that when a node is updated (e.g., via onConnect wiring),
  // the version stored in selectedLogicNode (which props panel sees) is also updated.
  React.useEffect(() => {
    if (selectedLogicNode) {
        const latestNode = nodes.find(n => n.id === selectedLogicNode.id);
        if (latestNode && JSON.stringify(latestNode.data) !== JSON.stringify(selectedLogicNode.data)) {
            setSelectedLogicNode(latestNode);
        }
    }
  }, [nodes, selectedLogicNode]);
  
  // Check if instrument is F&O
  const isFnO = selectedInstrument && DERIVATIVE_INSTRUMENT_TYPES.includes(
      (selectedInstrument.instrument_type || selectedInstrument.type || '').toUpperCase()
  );

  //! iter-7 — the instrument handed to the OrderCard carries the continuous flag so the card can
  //! scope its expired "reselect" hint to NON-continuous instruments (a continuous strategy
  //! auto-rolls, so it must never tell the user to reselect). Memoized (not spread inline in JSX) to
  //! preserve OrderCard's memo — the ref only changes when selectedInstrument or isContinuous does.
  //! Display-only: isContinuous is NOT written into selectedInstrument STATE (it would leak a
  //! redundant field into the persisted config.instrument); it lives on config.general.isContinuous.
  const orderCardInstrument = useMemo(
    () => (selectedInstrument
      ? { ...selectedInstrument, isContinuous: Boolean(instrumentConfig?.isContinuous) }
      : selectedInstrument),
    [selectedInstrument, instrumentConfig?.isContinuous],
  );

  const [isSaving, setIsSaving] = useState(false);
  const saveInProgressRef = useRef(false); // Synchronous guard to prevent StrictMode double-firing

  const handleChartError = useCallback((msg) => {
      console.error('[StrategyBuilder] Chart Error:', msg);
      showSnackbar(msg, 'error');
  }, [showSnackbar]);

  //--> One current-config authority for preview and persistence. SmartChart sends
  //*   these exact blocks to /calculate; save persists the same object values.
  const currentInstruments = useMemo(
    () => (selectedInstrument ? [selectedInstrument] : []),
    [selectedInstrument],
  );
  const currentGeneral = useMemo(() => ({
    executionType: strategyInfo.executionType,
    dateRange: strategyInfo.dateRange,
    isContinuous: instrumentConfig.isContinuous,
    rolloverDays: instrumentConfig.rolloverDays,
    autoCloseOut: instrumentConfig.autoCloseOut,
    activeChartType,
    activeStep,
    brokerAccounts: strategyInfo.executionConfig?.brokerAccounts || [],
  }), [
    strategyInfo.executionType,
    strategyInfo.dateRange,
    strategyInfo.executionConfig?.brokerAccounts,
    instrumentConfig.isContinuous,
    instrumentConfig.rolloverDays,
    instrumentConfig.autoCloseOut,
    activeChartType,
    activeStep,
  ]);

  //--> Chart preview gets the stored config's functional `general` only — the UI-only
  //*   step / chart-type fields must not count as a config change (re-runs /calculate).
  const chartGeneral = useMemo(() => {
    const { activeChartType: _uiChartType, activeStep: _uiStep, ...functionalGeneral } = currentGeneral;
    return functionalGeneral;
  }, [currentGeneral]);

  const handleSaveStrategy = async () => {
      //--> Guard against double-clicks AND React StrictMode double-firing
      if (isSaving || saveInProgressRef.current) return;
      
      try {
          saveInProgressRef.current = true; // Set immediately (synchronous)
          setIsSaving(true); // Set state (async - for UI feedback)

        // Normalize indicator timeframe with connected data_source before save.
        // This prevents stale params.timeframe from being persisted when source
        // rewires occurred through edges/source selection.
        const nodeMap = new Map((nodes || []).map(n => [n.id, n]));
        const normalizedNodes = (nodes || []).map(node => {
          if (node.type !== 'indicator') return node;

          const incomingEdges = (edges || []).filter(e => e.target === node.id);
          const primaryEdge = incomingEdges.find(e => isPrimaryInputHandle(e.targetHandle)) || incomingEdges[0];
          const sourceId = primaryEdge?.source || node.data?.sourceId;
          const sourceNode = sourceId ? nodeMap.get(sourceId) : null;
          if (!sourceNode || sourceNode.type !== 'data_source') return node;

          const sourceTf = sourceNode.data?.timeframe;
          if (!sourceTf) return node;

          const currentTf = node.data?.timeframe;
          const currentParamTf = node.data?.params?.timeframe;
          if (currentTf === sourceTf && currentParamTf === sourceTf) return node;

          return {
            ...node,
            data: {
              ...node.data,
              sourceId,
              timeframe: sourceTf,
              params: {
                ...(node.data?.params || {}),
                timeframe: sourceTf,
              },
            },
          };
        });

          //! #18 money-path — mirror the entered LOT quantity (data.execution.quantity) into
          //  data.qty, the field the backend evaluator sizes from (graph_evaluator.py:1861).
          //  See mirrorExecutionQtyToData for the LOTS-not-units rationale.
          const qtyMirroredNodes = mirrorExecutionQtyToData(normalizedNodes);

          const payload = {
            name: strategyInfo.name,
            description: strategyInfo.description,
            tags: [],
            config: {
                general: currentGeneral,
                instruments: currentInstruments,
                nodes: qtyMirroredNodes,
                edges: sanitizeIncomingEdges(edges, qtyMirroredNodes),
                //--> per-account-order-settings R1.2.1 (02-design.md §2 Event flow R1 step 7) —
                //    { [nodeId]: { [accountId]: OrderConfig } }, authored in Step-4's TradeExecutionStep
                //    (orders useMemo) and mirrored upstream into strategyInfo.executionConfig.orders via
                //    TradeExecutionStep's onConfigChange. StrategyConfigSchema (backend) types this at
                //    the outer two levels; materializes into strategy_account_allocations at deploy time.
                orders: strategyInfo.executionConfig?.orders || {},
                compute_formula: null // Derived from graph
            }
          };

          // DEBUG: Print Strategy Structure before Saving as requested by User
          console.log(`[StrategyBuilder] saving strategy "${payload.name}" version ${strategyInfo.version || 'Draft'}... [nodes: ${payload.config.nodes.length}, edges: ${payload.config.edges.length}]`);
          if (payload.config && payload.config.nodes) {
              console.table(payload.config.nodes.map(n => ({
                  id: n.id,
                  type: n.type,
                  data: JSON.stringify(n.data).substring(0, 100) + '...' // truncate for readability
              })));
          }

          let response;
          if (strategyId) {
              // Update mode: 
              // 1. Update metadata (name, description)
              await StrategyService.updateStrategy(strategyId, payload);
              
              // 2. Save new version (nodes, edges, config)
              response = await StrategyService.saveVersion(strategyId, payload.config, 'Updated configuration', currentParentVersionId);
          } else {
              // Create mode: Backend handles first version creation automatically
              response = await StrategyService.createStrategy(payload);
          }
          
          if (response.status === 201 || response.status === 200 || response.data?.strategy_id || response.data?.version_id || response.data?.status === 'success') {
             showSnackbar(strategyId ? 'Strategy version updated!' : 'Strategy created successfully!', 'success');
             
             // Extract newly created version ID
             const responseData = response.data?.data || response.data;
             const newVersionId = responseData?.version_id || responseData?.id;
             const newVersionNumber = responseData?.version_number;
             
             if (newVersionNumber) {
                 setStrategyInfo(prev => ({ ...prev, version: newVersionNumber }));
             }
             
             // Once saved, the new version becomes our parent for subsequent saves
             if (newVersionId) {
                 setCurrentParentVersionId(newVersionId);
                 
                 // Silently update browser URL so F5 refresh stays on this new version
                 const activeStrategyId = strategyId || responseData?.strategy_id || responseData?.id;
                 if (activeStrategyId) {
                     window.history.replaceState(null, '', `/strategies/edit/${activeStrategyId}/version/${newVersionId}`);
                 }
             }
          } else {
             showSnackbar('Failed to save strategy. Please check logs.', 'error');
             console.error('Failed to save strategy', response);
          }
      } catch (error) {
          //! F5.3 (dead-exit-readiness-gate) — a 422 GRAPH_VALIDATION_REJECTED carries the
          //  server's node-named validation errors (see submitDeploy's 422 handling above for
          //  the same detail.error.code unwrap pattern); surface those instead of the generic
          //  error.message so the user knows WHICH node is broken.
          const saveDetail = error?.response?.data?.detail;
          const saveCode = saveDetail?.error?.code;
          if (error?.response?.status === 422 && saveCode === 'GRAPH_VALIDATION_REJECTED') {
              const graphErrors = saveDetail?.error?.details?.errors;
              if (Array.isArray(graphErrors) && graphErrors.length > 0) {
                  showSnackbar(graphErrors.map((e) => e.message).join(' · '), 'error');
                  console.error('Error saving strategy:', error);
                  return;
              }
          }
          showSnackbar('Error saving strategy: ' + (error.message || 'Unknown error'), 'error');
          console.error('Error saving strategy:', error);
      } finally {
          setIsSaving(false);
          saveInProgressRef.current = false; // Reset ref for next save
      }
  };

  // --> Auto-Arrange Handler
  const handleAutoArrange = () => {
      setNodes(currentNodes => {
          const newNodes = [...currentNodes];
          const categoriesNodes = {};
          
          // 1. Group by Category
          newNodes.forEach(node => {
              const cat = getLayoutCategory(node.type, node.data);
              if (!categoriesNodes[cat]) categoriesNodes[cat] = [];
              categoriesNodes[cat].push(node);
          });

          let maxLogicBottom = 0;
          const logicCategories = ['indicator', 'math', 'comparator', 'logic_gate'];

          // 2. Pass 1: Position Logic Categories (Columns) and track the absolute bottom
          logicCategories.forEach(cat => {
              const catNodes = categoriesNodes[cat];
              if (!catNodes) return;
              
              const config = LAYOUT_CONFIG[cat];
              catNodes.sort((a, b) => a.position.y - b.position.y);
              
              let currentY = config.startY;
              catNodes.forEach(node => {
                  const h = calculateNodeHeight(node);
                  
                  node.position = { x: config.x, y: currentY };
                  currentY += h + PADDING_Y;
                  if (currentY > maxLogicBottom) maxLogicBottom = currentY;
              });
          });

          // 3. Pass 2: Position everything else (Data Source, Visuals, Execution, Output)
          // We iterate over categoriesNodes again to ensure we don't skip anything
          Object.keys(categoriesNodes).forEach(cat => {
              if (logicCategories.includes(cat)) return; // Already positioned in Pass 1

              const catNodes = categoriesNodes[cat];
              const config = LAYOUT_CONFIG[cat] || LAYOUT_CONFIG['other'];
              catNodes.sort((a, b) => a.position.y - b.position.y);

              let currentY = config.startY;
              
              // SPECIAL: Visual and Output Nodes Waterfall below Logic
              if (cat === 'visual' || cat === 'output') {
                  currentY = Math.max(config.startY, maxLogicBottom + 200);
              }

              catNodes.forEach(node => {
                  const h = calculateNodeHeight(node);

                  node.position = { x: config.x, y: currentY };
                  currentY += h + PADDING_Y;
              });
          });

          return newNodes;
      });
      
      showSnackbar('Layout auto-arranged!', 'success');
  };

  // --> Toolbox Item Click Handler (Refactored to use Factory)
  const { createNode } = useNodeFactory(nodes, setNodes, setEdges);

  // Custom Interceptor for onNodesChange to handle auto-stacking on removal
  const handleNodesChange = useCallback((changes) => {
      const allowedChanges = filterNodeChangesByRole(
        changes, nodes, isSuperuser, edges,
      );
      if (allowedChanges.length === 0) return [];
      // 1. Apply changes first (ReactFlow standard)
      onNodesChange(allowedChanges);
      
      // 2. Check for Removals
      const removals = allowedChanges.filter(c => c.type === 'remove');
      if (removals.length > 0) {
          const removedIds = new Set(removals.map(c => c.id));
          const removedNodes = nodes.filter(n => removedIds.has(n.id)); 
          
          const affectedCategories = new Set();
          removedNodes.forEach(n => {
               const cat = getLayoutCategory(n.type, n.data);
               if (cat === 'data' || cat === 'visual') affectedCategories.add(cat);
          });
          
          if (affectedCategories.size > 0) {
              console.log('[Auto-Layout] Detected removal in categories:', Array.from(affectedCategories));
              setTimeout(() => {
                  setNodes(currentNodes => {
                      let nextNodes = [...currentNodes];
                      affectedCategories.forEach(cat => {
                          const catNodes = nextNodes.filter(n => getLayoutCategory(n.type, n.data) === cat)
                                                    .sort((a, b) => a.position.y - b.position.y);
                          if (catNodes.length > 0) {
                              const config = LAYOUT_CONFIG[cat];
                              let currentY = config.startY;
                              if (cat === 'data') {
                                   const primary = catNodes.find(n => !n.data.isSecondary);
                                   if (primary) currentY = primary.position.y;
                              }
                              if (cat === 'visual') {
                                   currentY = catNodes[0].position.y;
                              }
                              const newPositions = {};
                              catNodes.forEach(node => {
                                  let h = calculateNodeHeight(node);
                                  newPositions[node.id] = { ...node.position, x: config.x, y: currentY };
                                  currentY += h + PADDING_Y;
                              });
                              nextNodes = nextNodes.map(n => newPositions[n.id] ? { ...n, position: newPositions[n.id] } : n);
                          }
                      });
                      return nextNodes;
                  });
              }, 10);
          }
      }
      return allowedChanges;
  }, [onNodesChange, nodes, edges, isSuperuser, setNodes]);

  //* ONE wiring authority for Canvas, Tree, Outline and the properties delete, reading the SAME role-locked
  //  projection the canvas renders (model nodes are connectable:false for non-superusers), so every surface
  //  refuses the same connections before any edge or node-data write.
  const graphWiring = useGraphWiring({
      nodes: canvasNodes,
      edges,
      setNodes,
      setEdges,
      onNodesChange: handleNodesChange,
      onEdgesChange,
  });

  const handleCanvasSubViewChange = useCallback((subView) => {
      setCanvasSubView(subView);
      setLogicViewMode('flow');
      if (isRightPaneCollapsed) setIsRightPaneCollapsed(false);
  }, [isRightPaneCollapsed, setCanvasSubView]);

  const handleLocateLogicNode = useCallback((node) => {
      if (!node) return;
      setSelectedLogicNode(node);
      setCanvasSubView('flow');
      setLogicViewMode('flow');
      setCanvasFocusRequest((request) => request + 1);
  }, [setCanvasSubView]);

  const handleSelectTreeAuthoringNode = useCallback((node) => {
      if (!node) return;
      setSelectedLogicNode(node);
      setLogicRightTab('builder');
      if (isRightPaneCollapsed) setIsRightPaneCollapsed(false);
  }, [isRightPaneCollapsed]);

  //* Tree, Outline and properties delete run main's canvas node delete (graphWiring.deleteNode); a locked node is
  //  refused there by the role filter, so the selection is kept for it.
  const handleDeleteLogicNode = useCallback((nodeId) => {
      const target = canvasNodes.find((node) => node.id === nodeId);
      if (!target || target.deletable === false || target.connectable === false) {
          return { ok: false, reason: 'Locked nodes cannot be deleted.' };
      }
      const outcome = graphWiring.deleteNode(nodeId);
      if (!outcome?.ok) {
          showSnackbar(outcome?.reason || 'This node cannot be deleted.', 'info');
          return outcome;
      }
      if (selectedLogicNode?.id === nodeId) setSelectedLogicNode(null);
      return outcome;
  }, [canvasNodes, graphWiring, selectedLogicNode?.id, showSnackbar]);


  const handleToolboxItemClick = (type, data, options = {}) => {
      // --> Context Aware Visibility
      // If we are in R&D Mode (Analyzing data), default new indicators to VISIBLE.
      // If we are in Logic Flow Mode (Building graph), default new indicators to HIDDEN (to avoid chart clutter).
      const contextOptions = {
          ...options,
          initialVisible: logicViewMode === 'rd'
      };

      // Use consolidated Factory
      const result = createNode(type, null, { ...data, ...contextOptions });
      
      
      if (!result.success) {
          showSnackbar(result.message, 'info');
      } else {
          // UI Feedback & Selection
          if (result.autoUpgraded && result.autoUpgradedLabels?.length > 0) {
              // Tell the user what was silently promoted so the auto-upgrade is transparent.
              const upgraded = result.autoUpgradedLabels.join(', ');
              showSnackbar(
                  `"${upgraded}" upgraded to Multi-stage \u2192 ${result.node.data.label} added as Stage ${result.node.data.stage}`,
                  'info'
              );
          } else {
              showSnackbar(`${result.node.data.label} added`, 'success');
          }
          setSelectedLogicNode(result.node);
      }
  };

  const handleDashboardItemClick = (node) => {
      // Safely resolve full node from graph if only ID is provided (maintains properties context)
      const fullNode = nodes.find(n => n.id === node.id) || node;
      setSelectedLogicNode(fullNode);
      
      // Feature: Automatically switch to Builder tab if we were looking at backtest results
      if (logicRightTab !== 'builder') {
          setLogicRightTab('builder');
      }

      // Feature: Auto-expand sidebar if collapsed
      if (isRightPaneCollapsed) {
          setIsRightPaneCollapsed(false);
      }
  };

  // Dashboard Mode: Live vs Interactive
  const [isDashboardLive, setIsDashboardLive] = useState(false);

  const toggleDashboardLive = () => {
      setIsDashboardLive(prev => {
          const next = !prev;
          if (next) {
              setHoverData(null); // Clear frozen data when entering Live Mode
          }
          return next;
      });
  };

  const handleCrosshairMove = useCallback((params) => {
      // 1. Live Mode: Ignore all hover events (dashboard locked to latestData)
      if (isDashboardLive) return;

      // 2. Interactive Mode:
      // If params is null (mouse leaved chart), DO NOT CLEAR hoverData
      // This implements "Stickiness" - keeping the last inspected candle visible
      if (!params || !params.time) {
          // setHoverData(null); // DISABLED for stickiness
          setCrosshairPos(null); // Clear visual crosshair on chart though
          return;
      }
      
      // --> Fix for "Flashing" / Data Loss during Zoom
      const hasMetricData = params.open !== undefined || params.close !== undefined || params.value !== undefined || Object.keys(params).some(k => !['time', 'point', 'sourceId'].includes(k));

      if (!hasMetricData) {
          // Valid Coordinate, but NO Data.
          return;
      }
      
      setCrosshairPos(params);
      
      // DIRECT DATA FLOW: Use the enriched data object from SmartChart
      setHoverData(params);
  }, [isDashboardLive]);
  
  const handlePopOut = () => {
      // 1. Determine Pop-out URL
      // We pass the strategyId so the Pop-out can listen to the same BroadcastChannel
      const url = `/strategies/${strategyId}/dashboard-popout`;
      
      // 2. Open Window
      const width = 450;
      const height = 800;
      const left = window.screen.width / 2 + 100;
      const top = 100;
      
      window.open(url, `dashboard_${strategyId}`, `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };



  const handleInstrumentSelect = (data) => {
      // data can be { instrument, brokerSource } or just instrument depending on selector usage
      const instrument = data.instrument || data;
      setSelectedInstrument(instrument);
      setIsSelectorOpen(false);
      
      // Reset config
      setInstrumentConfig({
          isContinuous: false,
          rolloverDays: 2,
          autoCloseOut: true, //! R4 Hop-1 (money-path #30) — default ON
          squareOffTime: "", //! #140/#141 E5 — platform default until overridden
          squareOffLeadMinutes: ""
      });
      // Reset chart types to default
      setVisibleChartTypes(['candle', 'line']);
  };

  // Helper to categorize nodes for the R&D Dashboard (Delegated to shared utils for consistency)
  const categorizedNodes = useMemo(() => categorizeNodes(effectiveNodes, edges), [effectiveNodes, edges]);
  //--> A visible Tree/Outline always receives current node objects (including positions). When only the
  //    shared-by notice needs the model, reuse structurally equal inputs across position-only drag ticks.
  const treeSurfaceVisible = logicViewMode === 'flow' && (canvasSubView === 'tree' || outlineOpen);
  const noticeTreeNodesRef = useRef([]);
  const noticeTreeNodes = useMemo(() => {
      if (!sameTreeModelInputs(noticeTreeNodesRef.current, canvasNodes)) {
          noticeTreeNodesRef.current = canvasNodes;
      }
      return noticeTreeNodesRef.current;
  }, [canvasNodes]);
  const treeModelNodes = treeSurfaceVisible
      ? canvasNodes
      : (selectedLogicNode ? noticeTreeNodes : null);
  const builderTreeModel = useMemo(
      //! 50-21 — the 4th argument is the record (node id -> entry) the display-name resolver reads
      () => (treeModelNodes ? buildBuilderTreeModel(treeModelNodes, edges, readinessItems, visualStore.effectiveOverrides) : EMPTY_TREE_MODEL),
      [treeModelNodes, edges, readinessItems, visualStore.effectiveOverrides],
  );

  // --> Dynamic Field Metadata (Backend + Discovery)
  const [backendFields, setBackendFields] = useState({ standard: [], calculated: [] });

  // Fetch standard fields from Backend Schema on mount
  useEffect(() => {
      let isMounted = true;
      const fetchFields = async () => {
          try {
              const res = await StrategyService.getMetadataFields();
              if (isMounted && res.data && res.data.data) {
                  setBackendFields(res.data.data);
              }
          } catch (err) {
              console.warn("[StrategyBuilder] Failed to load field metadata, using defaults", err);
          }
      };
      fetchFields();
      return () => { isMounted = false; };
  }, []);

  // Compute Final Available Data Fields
  // Merges Backend Schema with any live fields discovered in incoming ticks (latestData)
  // Optimization: Decouple from hoverData to prevent re-calc on every mouse move
  const dataToScan = (dataSchema && Object.keys(dataSchema).length > 0) ? dataSchema : latestData;

  const availableFieldKeys = useMemo(() => {
      if (!dataToScan) return '';
      return Object.keys(dataToScan).sort().join(',');
  }, [dataToScan]);

  const availableDataFields = useMemo(() => {
      // 1. Base Lists (Backend or Fallback)
      const standardBase = backendFields.standard.length > 0 
          ? backendFields.standard 
          : ['OPEN', 'HIGH', 'LOW', 'CLOSE', 'VOLUME', 'VWAP', 'TRADE_COUNT'];
      
      const calculatedBase = backendFields.calculated.length > 0
          ? backendFields.calculated
          : ['HL2', 'HLC3', 'OHLC4', 'OC2'];

      // 2. Discover extra fields from stable data source
      const dynamic = Object.keys(dataToScan || {})
          .filter(key => {
              const val = dataToScan[key];
              const isNumeric = typeof val === 'number' || (typeof val === 'string' && !isNaN(parseFloat(val)) && isFinite(val));
              
              if (['time', '_id', 'timestamp', 'instrument_token', 'created_at', 'received_at', 'oid', 'rid'].includes(key)) return false;
              if (key.endsWith('_at')) return false; 
              
              // Only include if NOT already in standard/calculated lists
              return isNumeric && !standardBase.includes(key) && !calculatedBase.includes(key);
          });

      return {
          standard: [...standardBase, ...dynamic], // Dynamic fields treated as standard inputs
          calculated: calculatedBase
      };
  }, [backendFields, availableFieldKeys]); // Removed dataSchema dependency to rely on computed key string


  //--> Backtest trade markers — shared builder; naive ISO timestamps parsed as UTC
  //    to align with SmartChart candle parsing.
  const tradeMarkers = useMemo(
    () => buildMarkersFromTrades(backtestResults?.trades, { nodes }),
    [backtestResults, nodes],
  );

  //--> Trades with level_history for chart level visualization
  const tradesWithLevels = useMemo(() => {
      const trades = backtestResults?.trades;
      if (!trades) return null;
      return trades.filter((t) => t.level_history && t.level_history.length > 0);
  }, [backtestResults]);

  //--> Extract exit nodes for level display config
  const exitNodes = useMemo(() => {
      if (!nodes) return null;
      return nodes.filter((n) => ['sl', 'tp', 'time_exit'].includes(n.data?.action));
  }, [nodes]);

  // Render Functions
  const renderRightPaneContent = () => {
    switch(activeStep) {
      case 'general':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-sm font-extrabold text-fg-1 tracking-wide uppercase mb-2">
                Step 1: Strategy Details
              </h3>
              <p className="text-base text-fg-3 leading-relaxed">
                Define the core identity of your strategy.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-fg-1 uppercase tracking-wider mb-3">
                  Strategy Name
                </label>
                <input 
                  type="text" 
                  value={strategyInfo.name}
                  onChange={(e) => setStrategyInfo({...strategyInfo, name: e.target.value})}
                  placeholder="e.g. Mean Reversion BTC"
                  className="w-full px-4 py-3 rounded-xl border border-[var(--divider)] bg-white dark:bg-bg-2 focus:ring-2 focus:ring-accent outline-none transition-all font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-fg-1 uppercase tracking-wider mb-3">
                  Description
                </label>
                <textarea 
                  value={strategyInfo.description}
                  onChange={(e) => setStrategyInfo({...strategyInfo, description: e.target.value})}
                  placeholder="Explain the logic..."
                  className="w-full px-4 py-3 rounded-xl border border-[var(--divider)] bg-white dark:bg-bg-2 focus:ring-2 focus:ring-accent outline-none transition-all h-32 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-fg-1 uppercase tracking-wider mb-3">
                  Execution Type
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => setStrategyInfo({...strategyInfo, executionType: 'AUTOMATED'})}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
                      strategyInfo.executionType === 'AUTOMATED'
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-[var(--divider)] hover:border-[var(--divider)]'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      strategyInfo.executionType === 'AUTOMATED' ? 'bg-accent/20 text-accent' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}>
                      <FiZap size={20} />
                    </div>
                    <div>
                      <span className="block font-bold mb-0.5">Automated</span>
                      <span className="text-xs opacity-80">Full algorithmic execution</span>
                    </div>
                  </button>

                  <button
                    onClick={() => setStrategyInfo({...strategyInfo, executionType: 'SEMI_AUTO'})}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
                      strategyInfo.executionType === 'SEMI_AUTO'
                        ? 'border-[var(--theme-purple)] bg-[color-mix(in_oklch,var(--theme-purple)_10%,transparent)] text-[var(--theme-purple)]'
                        : 'border-[var(--divider)] hover:border-[var(--divider)]'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      strategyInfo.executionType === 'SEMI_AUTO' ? 'bg-[color-mix(in_oklch,var(--theme-purple)_20%,transparent)] text-[var(--theme-purple)]' : 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                    }`}>
                      <FiBell size={20} />
                    </div>
                    <div>
                      <span className="block font-bold mb-0.5">Semi-Auto</span>
                      <span className="text-xs opacity-80">Signal approval required</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setActiveStep('instruments')}
                disabled={!strategyInfo.name}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                Next Step <FiChevronRight />
              </button>
            </div>
          </div>
        );

      case 'instruments':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-sm font-extrabold text-fg-1 tracking-wide uppercase mb-2">
                Step 2: Instruments
              </h3>
              <p className="text-base text-fg-3 leading-relaxed">
                Configure the assets and data properties.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-xs font-bold text-fg-1 uppercase tracking-wider">
                    Selected Instrument
                  </label>
                  {selectedInstrument && (
                    <button 
                      onClick={() => setIsSelectorOpen(true)}
                      className="text-xs text-accent font-bold hover:underline"
                    >
                      Change
                    </button>
                  )}
                </div>

                {!selectedInstrument ? (
                   <div 
                     onClick={() => setIsSelectorOpen(true)}
                     className="border-2 border-dashed border-[var(--divider)] rounded-xl p-8 flex flex-col items-center justify-center text-fg-3 hover:border-accent hover:bg-accent/10 transition-all cursor-pointer group"
                   >
                       <div className="w-12 h-12 rounded-full bg-bg-3 dark:bg-bg-2 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                           <FiPlus size={24} />
                       </div>
                       <p className="font-bold text-sm">Select Asset</p>
                   </div>
                ) : (
                  <div className="bg-white dark:bg-bg-2 rounded-xl p-4 shadow-sm border border-[var(--divider)]">
                      <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-bold text-fg-1">{selectedInstrument.symbol}</h4>
                          <span className="px-1.5 py-0.5 bg-accent/10 text-accent text-[10px] rounded font-bold">
                              {selectedInstrument.exchange}
                          </span>
                      </div>
                      <p className="text-fg-3 text-xs">{selectedInstrument.name}</p>
                      
                      {/* Date display moved to separate section */}
                      
                  </div>
                )}
              </div>


              {selectedInstrument && isFnO && (
                  <div className="bg-bg-3 dark:bg-bg-2/50 border border-[var(--divider)] rounded-xl p-4">
                      <h4 className="font-bold text-xs text-fg-1 flex items-center gap-2 mb-3 uppercase tracking-wider">
                          <FiSettings className="text-fg-3" /> Contract Settings
                      </h4>
                      <div className="space-y-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                              <div className="relative">
                                  <input 
                                    type="checkbox" 
                                    checked={instrumentConfig.isContinuous}
                                    onChange={(e) => setInstrumentConfig({...instrumentConfig, isContinuous: e.target.checked})}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-bg-3 peer-focus:outline-none rounded-full peer dark:bg-bg-3 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--divider)] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[var(--theme-purple)]"></div>
                              </div>
                              <span className="text-sm font-medium text-fg-1">Continuous Contract</span>
                          </label>

                          {instrumentConfig.isContinuous && (
                              <div>
                                  <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider">
                                      Rollover Logic
                                    </label>
                                    <span className="text-xs font-mono font-bold text-[var(--theme-purple)] bg-[color-mix(in_oklch,var(--theme-purple)_14%,transparent)]/30 px-2 py-0.5 rounded">
                                      Expiry - {instrumentConfig.rolloverDays} days
                                    </span>
                                  </div>
                                  
                                  <div className="relative pt-1 pb-6 select-none">
                                    <input 
                                      type="range" 
                                      min="0"
                                      max="5"
                                      step="1"
                                      value={instrumentConfig.rolloverDays}
                                      onChange={(e) => setInstrumentConfig({...instrumentConfig, rolloverDays: parseInt(e.target.value)})}
                                      className="w-full h-2 bg-bg-3 rounded-lg appearance-none cursor-pointer dark:bg-bg-3 accent-indigo-600 dark:accent-indigo-500 relative z-10 caret-transparent focus:outline-none focus:ring-0"
                                    />
                                    <div className="absolute top-5 left-0 w-full h-4 text-[10px] text-fg-3 font-bold mt-2 pointer-events-none">
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(0% + 8px)' }}>Expiry</span>
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(20% + 4.8px)' }}>1d</span>
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(40% + 1.6px)' }}>2d</span>
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(60% - 1.6px)' }}>3d</span>
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(80% - 4.8px)' }}>4d</span>
                                      <span className="absolute -translate-x-1/2" style={{ left: 'calc(100% - 8px)' }}>5d</span>
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-fg-3 mt-2">
                                    Contracts will typically be rolled over {instrumentConfig.rolloverDays} days before the official expiry date.
                                  </p>
                              </div>
                          )}

                          {/* R4 Hop-1 (money-path #30) — per-strategy auto-close-out toggle.
                              Applies uniformly to BOTH the continuous roll-switch close (R3.2)
                              AND the pinned T-1 pre-expiry close (R3.3); persisted to
                              config.general.autoCloseOut, read at runtime by
                              instance_manager.py's add_instance (Hop-2). Decision surface —
                              display == runtime authority (feedback_display_is_a_decision_surface). */}
                          <label className="flex items-center gap-3 cursor-pointer">
                              <div className="relative">
                                  <input
                                    type="checkbox"
                                    checked={instrumentConfig.autoCloseOut}
                                    onChange={(e) => setInstrumentConfig({...instrumentConfig, autoCloseOut: e.target.checked})}
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-bg-3 peer-focus:outline-none rounded-full peer dark:bg-bg-3 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--divider)] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[var(--theme-purple)]"></div>
                              </div>
                              <span className="text-sm font-medium text-fg-1">Auto-close open positions before contract rollover/expiry</span>
                          </label>
                          {!instrumentConfig.autoCloseOut && (
                              <p className="text-[10px] text-amber-500 mt-1">
                                Positions will NOT be auto-closed and may settle to zero or strand on the old/expiring contract.
                              </p>
                          )}
                      </div>
                  </div>
              )}

              {/* Backtesting Configuration Section */}
              <div className={`transition-all border rounded-xl p-4 ${
                  strategyInfo?.dateRange?.start 
                    ? 'bg-[color-mix(in_oklch,var(--theme-purple)_10%,transparent)]/10 border-[color-mix(in_oklch,var(--theme-purple)_25%,transparent)]/30'
                    : 'bg-bg-3 dark:bg-bg-2 border-[var(--divider)] border-dashed'
              }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        strategyInfo?.dateRange?.start 
                            ? 'bg-[color-mix(in_oklch,var(--theme-purple)_14%,transparent)]/50 text-[var(--theme-purple)]'
                            : 'bg-bg-3 dark:bg-bg-3 text-fg-3'
                    }`}>
                      <FiClock size={16} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-xs uppercase tracking-wider ${
                          strategyInfo?.dateRange?.start ? 'text-[var(--theme-purple)]' : 'text-fg-3'
                      }`}>
                        Backtesting Period
                      </h4>
                      <p className={`text-[10px] ${
                          strategyInfo?.dateRange?.start ? 'text-[var(--theme-purple)]' : 'text-fg-3'
                      }`}>
                        {strategyInfo?.dateRange?.start ? 'Simulation timeline' : 'Required for backtest'}
                      </p>
                    </div>
                  </div>
                  
                  {strategyInfo?.dateRange?.start ? (
                      <div className="bg-white dark:bg-bg-2 rounded-lg p-3 border border-[color-mix(in_oklch,var(--theme-purple)_25%,transparent)]/50 shadow-sm animate-fadeIn">
                        <div className="flex items-center gap-3">
                             <FiCalendar className="text-[var(--theme-purple)] text-lg flex-shrink-0" />
                             <div>
                                <div className="text-[10px] font-bold text-fg-3 uppercase tracking-wide">Selected Range</div>
                                <div className="text-sm font-mono font-bold text-fg-1">
                                    {strategyInfo.dateRange.start} 
                                    <span className="mx-2 text-fg-3">→</span> 
                                    {strategyInfo.dateRange.end ? strategyInfo.dateRange.end : <span className="italic text-fg-3">Single Day</span>}
                                </div>
                             </div>
                        </div>
                      </div>
                  ) : (
                      <div className="text-center p-4 bg-white/50 dark:bg-bg-2/50 rounded-lg">
                          <p className="text-sm font-medium text-fg-3">
                             Please select a date range from the <span className="font-bold text-fg-1">Data Availability Preview</span> on the left.
                          </p>
                      </div>
                  )}
              </div>

              <div className="pt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActiveStep('general')}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setActiveStep('logic')}
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  Next: Logic <FiChevronRight />
                </button>
              </div>
            </div>
            
            <InstrumentSelector 
                open={isSelectorOpen} 
                onClose={() => setIsSelectorOpen(false)}
                onSelect={handleInstrumentSelect}
                title="Select Strategy Instrument"
            />
          </div>
        );


          case 'logic':
            // Find Data Source Node for global config
            const dataSourceNode = nodes.find(n => n.type === 'data_source');
            const hasExecutionNodes = nodes.some(n => ['signal', 'position_trigger'].includes(n.type));
            
            return (
               <div className="space-y-6 h-full flex flex-col">
                  {/* Step 3 Header with View Mode Toggle */}
                  <div className="flex flex-col gap-4 pb-4 border-b border-[var(--divider)] shrink-0">
                      <div className="flex justify-between items-center">
                          <h3 className="text-sm font-extrabold text-fg-1 tracking-wide uppercase">
                            Step 3: Strategy Logic
                          </h3>
                      </div>
                      
                      {/* Builder / Backtest Tabs */}
                      <div className="flex p-1 bg-bg-3 dark:bg-bg-2 rounded-lg">
                          <button
                            onClick={() => setLogicRightTab('builder')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold transition-all ${
                                logicRightTab === 'builder'
                                    ? 'bg-white dark:bg-bg-3 text-accent shadow-sm'
                                    : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                            }`}
                          >
                             <FiCpu size={14} /> Builder
                          </button>
                          <button
                            onClick={() => {
                                if (hasExecutionNodes) setLogicRightTab('backtest');
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold transition-all ${
                                logicRightTab === 'backtest'
                                    ? 'bg-white dark:bg-bg-3 text-[var(--theme-purple)] shadow-sm'
                                    : hasExecutionNodes
                                        ? 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                                        : 'text-fg-3 opacity-50 cursor-not-allowed'
                            }`}
                            title={!hasExecutionNodes ? "Add execution nodes to enable backtesting" : ""}
                          >
                             <FiActivity size={14} /> Backtest
                          </button>
                      </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {logicRightTab === 'builder' ? (
                       <div className="h-full flex flex-col">
                           {selectedLogicNode && !canEditNodeForRole(selectedLogicNode, isSuperuser) ? (
                              <div data-testid="locked-model-node" className="m-4 rounded-lg border border-[var(--divider)] bg-bg-3 p-4 text-sm text-fg-2">
                                  This model node is locked. An admin can change or remove its binding.
                              </div>
                           ) : selectedLogicNode ? (
                              <>
                              <NodePropertiesPanel
                                  selectedNode={effectiveSelectedLogicNode}
                                  onClose={() => setSelectedLogicNode(null)}
                                  updateNodeData={updateNodeData}
                                  onDeleteNode={handleDeleteLogicNode}
                                  availableFields={availableDataFields}
                                  nodes={effectiveNodes}
                                  /* R9 — Name/Notes are writable only where display writes persist to the record
                                     (R10-T8: never node data); unsaved / unreadable / not-editable -> disabled. */
                                  strategyId={persistsDisplay ? strategyId : null}
                                  displayLockHint={displayLockHint}
                                  visualOverrides={visualStore.effectiveOverrides}
                                  edges={edges}
                                  onEdgeUpdate={setEdges}
                                  graphWiring={graphWiring}
                                  allIndicatorMetadata={allIndicatorMetadata}
                                  strategyScope={modelVersionScope}
                              />
                              <SharedByTriggersNotice
                                  nodeId={selectedLogicNode.id}
                                  model={builderTreeModel}
                              />
                              </>
                           ) : (
                                /* No Node Selected -> Show Toolbox */
                                <StrategyToolbox 
                                    onItemClick={handleToolboxItemClick} 
                                    allIndicators={authorizedToolboxIndicators}
                                    loading={isMetadataLoading}
                                />
                           )}
                      </div>
                    ) : (
                        <div className="h-full">
                            <BacktestStepRight
                                strategyId={strategyId}
                                config={backtestConfig}
                                setConfig={setBacktestConfig}
                                onRun={handleRunBacktest}
                                onLoadRun={handleLoadPreviousRun}
                                isRunning={isBacktesting}
                                progress={backtestProgress}
                                results={backtestResults}
                                logs={backtestLogs}
                                dateRange={strategyInfo.dateRange}
                                setSelectedTimeRange={handlePeriodSelect}
                                onBack={() => setLogicRightTab('builder')}
                                isMetricsExpanded={isMetricsExpanded}
                                onToggleExpanded={handleToggleMetricsExpanded}
                                exchangeTimezone={exchangeTimezone}
                            />
                        </div>
                    )}
                  </div>

                  {/* Default / Actions Footer (Always Visible) */}
                  <div className="pt-4 border-t border-[var(--divider)] space-y-4 shrink-0">
                      <h4 className="text-xs font-bold text-fg-3 uppercase tracking-widest">Actions</h4>
                      
                      {/* Backtest Progress (Visible when running backtest) */}
                      <AnimatePresence>
                          {isBacktesting && (
                              <motion.div 
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="w-full flex flex-col gap-1 overflow-hidden"
                              >
                                  <div className="flex justify-between text-[10px] font-bold text-fg-3 uppercase">
                                      <span className="flex items-center gap-1">
                                          <div className="w-2 h-2 border border-accent border-t-transparent rounded-full animate-spin"></div> {backtestStage}
                                      </span>
                                      <span>{Math.round(backtestProgress)}%</span>
                                  </div>
                                  <div className="w-full bg-bg-3 dark:bg-bg-3 rounded-full h-1.5 overflow-hidden">
                                      <div 
                                          className="bg-accent h-1.5 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.7)] transition-all duration-300" 
                                          style={{ width: `${backtestProgress}%` }}
                                      >
                                          <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
                                      </div>
                                  </div>
                              </motion.div>
                          )}
                      </AnimatePresence>

                      <div className="flex flex-col gap-4">
                          <div className="flex gap-3">
                              <button
                                type="button"
                                onClick={() => setActiveStep('instruments')}
                                className="btn btn-secondary flex items-center gap-2 group"
                              >
                                  <FiChevronRight className="rotate-180 group-hover:-translate-x-0.5 transition-transform" />
                                  Back
                              </button>
                              
                              {logicRightTab === 'backtest' ? (
                                  <div className="flex-1">
                                      <button
                                          onClick={handleRunBacktest}
                                          className={`w-full py-3.5 px-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-[0_4px_12px_rgba(37,99,235,0.2)] hover:shadow-[0_6px_16px_rgba(37,99,235,0.3)] active:scale-[0.98] ${
                                              isBacktesting 
                                                  ? 'bg-loss text-white hover:opacity-90'
                                                  : 'bg-accent text-white hover:opacity-90'
                                          }`}
                                      >
                                          {isBacktesting ? (
                                              <><FiSquare className="animate-pulse" /> Stop Backtest</>
                                          ) : (
                                              <><FiPlay className="group-hover:scale-110 transition-transform" /> Run Backtest</>
                                          )}
                                      </button>
                                  </div>
                              ) : (
                                  <button
                                      type="button"
                                      onClick={() => setActiveStep('execution')}
                                      className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                                  >
                                      Next: Execution <FiChevronRight className="group-hover:translate-x-0.5 transition-transform" />
                                  </button>
                              )}
                          </div>
                          <button
                              type="button"
                              onClick={handleSaveStrategy}
                              disabled={isSaving}
                              className={`btn btn-primary w-full flex items-center justify-center gap-2 ${isSaving ? 'cursor-wait' : ''}`}
                          >
                              {isSaving ? (
                                  <>
                                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                      Saving...
                                  </>
                              ) : (
                                  <>
                                    <FiCheck /> Save Strategy
                                  </>
                              )}
                          </button>
                          <p className="text-[10px] text-fg-3 text-center">
                              Saving creates a new version if strategy logic or sizing changed.
                          </p>
                      </div>
                  </div>
               </div>
            );

          case 'execution':
            return (
               <div className="h-full flex flex-col">
                  <div className="space-y-6 flex-1 overflow-y-auto">
                      {/*>>> REVIEW & SAVE SECTION <<<*/}
                      <div className="space-y-5">
                          <div className="flex flex-col gap-3 pb-4 border-b border-divider/50">
                              <h3 className="text-sm font-extrabold text-fg-1 tracking-wide uppercase">
                                Step 4: Review & Deploy
                              </h3>
                              <p className="text-xs text-fg-3 leading-relaxed">
                                 Review your execution sizing on the main panel. Quantities and accounts selected here will dictate live trading behavior.
                              </p>
                          </div>

                          {/*>>> Deployment readiness checklist (R5.1-R5.7/R6.1) — R5.7 decommission:
                              replaces the legacy "Tally Gate" ✓/✗ badge (removed here) with one
                              itemized surface. Reactive: fed by the debounced .../readiness call
                              above (docs/pipelines/2026-07-31-strategy-deploy-readiness-checklist). <<<*/}
                          <ReadinessPanel
                              items={readinessItems}
                              loading={readinessLoading}
                              nodes={nodes}
                              onSelectNode={handleReadinessSelectNode}
                              hasStrategyId={!!strategyId}
                              error={readinessError}
                          />

                          {/*>>> Save + Back <<<*/}
                          <div className="space-y-2">
                              <div className="flex gap-3">
                                  <button
                                    type="button"
                                    onClick={() => setActiveStep('logic')}
                                    disabled={isSaving}
                                    className={`btn btn-secondary flex items-center gap-2 group ${isSaving ? 'cursor-not-allowed' : ''}`}
                                  >
                                      <FiChevronRight className="rotate-180 group-hover:-translate-x-0.5 transition-transform" />
                                      Back
                                  </button>
                                  <button
                                      type="button"
                                      onClick={handleSaveStrategy}
                                      disabled={isSaving}
                                      className={`btn btn-primary flex-1 flex items-center justify-center gap-2 ${isSaving ? 'cursor-wait' : ''}`}
                                  >
                                      {isSaving ? (
                                          <>
                                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                              Saving...
                                          </>
                                      ) : (
                                          <>
                                              <FiCheck /> Save Strategy
                                          </>
                                      )}
                                  </button>
                              </div>
                              <p className="text-[10px] text-fg-3 text-center">
                                  Saving creates a new version if strategy logic or sizing changed.
                              </p>
                          </div>
                      </div>

                      {/*>>> DEPLOYMENT SECTION <<<*/}
                      <DeploymentSection
                          driftPrompt={driftPrompt}
                          onResolveDrift={resolveDrift}
                          onDismissDrift={dismissDrift}
                          strategyId={strategyId}
                          strategyStatus={strategyInfo.status}
                          currentVersionId={currentParentVersionId}
                          currentVersionNumber={strategyInfo.version}
                          readinessItems={readinessItems}
                          hasUnsavedChanges={false} /* TODO: wire to real dirty-tracking if added later */
                          deploymentState={deploymentState}
                          onModeChange={(mode) => setDeploymentState(prev => ({ ...prev, selectedMode: mode }))}
                          onDeploy={handleDeploy}
                          onStop={handleStopDeployment}
                          /* per-account-order-settings R6.3 (QA loop-back-2) — the in-memory DRAFT's
                             per-account order VALUES, keyed by account_id, for the pre-deploy drift
                             banner's VALUE comparison against what's deployed (not just a count).
                             Flattened from config.orders[nodeId][accountId]; first authoring node
                             wins per account (mirrors the backend's governing-entry-node
                             materialization, deployment_service._build_deploy_allocation_payload_rows). */
                          draftOrders={(() => {
                              const byNode = strategyInfo.executionConfig?.orders || {};
                              const acc = {};
                              Object.values(byNode).forEach((nodeOrders) => {
                                  Object.entries(nodeOrders || {}).forEach(([accId, cfg]) => {
                                      if (acc[accId] === undefined && cfg) acc[accId] = cfg;
                                  });
                              });
                              return acc;
                          })()}
                      />

                      {/*>>> R5.6 — "deploy anyway" acknowledgement step. Shown when a LIVE deploy
                          would otherwise trip R4.4.2's server-side ack requirement (all REQUIRED
                          items pass, ≥1 RECOMMENDED item fails). Confirming resubmits the SAME
                          deployStrategy call with ack:true + the failing item ids + snapshot hash
                          (N-2 — server-fresh values whenever the server itself supplied them). <<<*/}
                      {ackConfirm && (
                          <div
                              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
                              onClick={() => setAckConfirm(null)}
                          >
                              <div
                                  className="bg-white dark:bg-bg-2 rounded-2xl p-6 max-w-md mx-4 shadow-2xl border border-[var(--divider)]"
                                  onClick={(e) => e.stopPropagation()}
                              >
                                  <div className="space-y-4">
                                      <div className="flex items-center gap-3">
                                          <div className="w-12 h-12 bg-[var(--badge-warn-bg)] rounded-xl flex items-center justify-center border border-[color-mix(in_oklch,var(--color-loss)_25%,transparent)] shrink-0">
                                              <FiAlertTriangle className="text-loss" size={22} />
                                          </div>
                                          <div>
                                              <h3 className="text-base font-bold text-fg-1">Deploy anyway?</h3>
                                              <p className="text-xs text-fg-3 mt-1">
                                                  The following recommended checks are failing. All required checks pass — you may deploy live anyway, but confirm you have reviewed these first.
                                              </p>
                                          </div>
                                      </div>
                                      <div className="rounded-xl border border-[var(--divider)] bg-bg-3 p-3 space-y-1.5 max-h-56 overflow-auto">
                                          {(ackConfirm.failingRecommended || []).map((item) => (
                                              <div key={item.id} className="text-xs">
                                                  <span className="font-semibold text-fg-1">{item.label}</span>
                                                  {item.reason ? <span className="text-fg-3"> — {item.reason}</span> : null}
                                              </div>
                                          ))}
                                      </div>
                                      <div className="flex gap-3 pt-1">
                                          <button
                                              type="button"
                                              onClick={() => setAckConfirm(null)}
                                              className="btn btn-secondary flex-1"
                                          >
                                              Cancel
                                          </button>
                                          <button
                                              type="button"
                                              onClick={confirmAckAndDeploy}
                                              className="btn btn-deploy-live flex-1"
                                          >
                                              Deploy Anyway
                                          </button>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      )}
                  </div>
               </div>
            );
            

      default: return null;
    }
  };

  const renderCenterPaneContent = () => (
      <div className={`h-full flex flex-col ${activeStep === 'logic' ? 'w-full p-0' : 'items-center justify-center p-8'} bg-bg-3 dark:bg-bg-1/50`}>
        {activeStep === 'general' && (
          <div className="text-center max-w-lg">
             <div className="w-24 h-24 bg-accent/10 text-accent rounded-3xl mx-auto flex items-center justify-center mb-6">
                <FiZap size={48} />
             </div>
             <h2 className="text-2xl font-bold text-fg-1 mb-2">Strategy Overview</h2>
             <p className="text-fg-3">
               Configure the basic details on the right to get started. This area will visualize your strategy's structure.
             </p>
          </div>
        )}

        {activeStep === 'instruments' && (
          <div className="w-full h-full flex flex-col">
             <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-fg-1 flex items-center gap-2">
                  <FiBarChart2 /> Data Availability Preview
                </h3>
             </div>
             
             {selectedInstrument ? (
               <div className="flex-1 overflow-hidden relative">
                  <div className="w-full h-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                     <HistoricalDataTimeline 
                         instrument={selectedInstrument} 
                         mode="selection"
                         initialRange={strategyInfo.dateRange}
                         onRangeChange={(range) => setStrategyInfo(prev => ({ ...prev, dateRange: range }))}
                     />
                  </div>
               </div>
             ) : (
                <div className="flex-1 flex items-center justify-center border-2 border-dashed border-[var(--divider)] rounded-2xl">
                    <p className="text-fg-3 font-medium">Select an instrument to view data availability</p>
                </div>
             )}
          </div>
        )}

        {activeStep === 'logic' && (
           <>
               {/* Fluid R&D Mode: Integrated Chart + Dashboard */}
               {/* PERSISTENCE FIX: Keep Chart mounted but hidden when in Flow mode to prevent reload/data loss */}
                <div className={`w-full h-full flex flex-col p-2 ${logicViewMode === 'rd' ? '' : 'hidden'}`}>
                   {/* Top Chart Section - Integrated Fluid Transition */}
                   <motion.div
                        initial={false}
                        animate={{
                            height: isMetricsExpanded ? 0 : isChartExpanded ? '100%' : '45%',
                            opacity: isMetricsExpanded ? 0 : 1,
                            marginBottom: isMetricsExpanded ? 0 : isChartExpanded ? 0 : 16,
                            marginTop: isMetricsExpanded ? 0 : 4,
                            pointerEvents: isMetricsExpanded ? 'none' : 'auto'
                        }}
                        transition={{
                            duration: 0.6,
                            ease: [0.4, 0, 0.2, 1] // Custom cubic-bezier for premium feel
                        }}
                        // Layout/animation shell only — NO card styling. SmartChart renders its
                        // own DS card (bg-bg-2 / border / rounded-lg / shadow); a second card here
                        // double-enclosed it and the hardcoded dark:bg-[#1a1d24] (a blue-tinted
                        // --fg-1 value misused as a background) showed through. overflow-hidden is
                        // kept only for the collapse-to-0 animation.
                        className="w-full min-h-0 overflow-hidden relative group shrink-0"
                   >
                       {/* Persistence Wrapper: keeps the chart DOM mounted through the collapse.
                           No min-height — the chart must fill THIS (card-sized) box so its bottom
                           volume + time axis aren't clipped. SmartChart's resize guard (skips
                           0-height) + lockVisibleTimeRangeOnResize already preserve it on collapse. */}
                       <div className="h-full w-full relative">
                            {/* Period Filter Indicator — shows when viewing a specific backtest period */}
                            {backtestSelectedTimeRange && (
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 bg-[var(--theme-purple)]/90 text-white text-xs font-bold rounded-full shadow-lg backdrop-blur-sm animate-fadeIn">
                                    <FiCalendar size={12} />
                                    <span>
                                        {formatDateInTimezone(backtestSelectedTimeRange.start, exchangeTimezone)} → {formatDateInTimezone(backtestSelectedTimeRange.end, exchangeTimezone)}
                                    </span>
                                    <Tooltip content="Show full range" placement="bottom">
                                    <button
                                        onClick={() => { lastPeriodSelectRef.current = null; setBacktestSelectedTimeRange(null); }}
                                        className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                                        aria-label="Show full range"
                                    >
                                        <FiX size={12} />
                                    </button>
                                    </Tooltip>
                                </div>
                            )}
                              {isBacktestStale && (
                                <div className="absolute bottom-2 left-2 z-10 px-3 py-1.5 bg-[var(--warning)] text-white text-xs font-semibold rounded-full shadow-lg backdrop-blur-sm">
                                  Strategy changed. Run backtest to refresh indicator details.
                                </div>
                              )}
                            {selectedInstrument && isBacktestCheckDone ? (
                                 <SmartChart
                                    canvasAction={(
                                        <button
                                            onClick={handleToggleChartExpanded}
                                            className="p-1 rounded-full bg-bg-3/80 dark:bg-bg-2/80 hover:bg-bg-3 text-fg-3 hover:text-fg-1 transition-colors shadow-sm border border-[var(--divider)]"
                                            aria-label={isChartExpanded ? 'Collapse chart' : 'Expand chart'}
                                        >
                                            {isChartExpanded ? <FiMinimize2 size={12} /> : <FiMaximize2 size={12} />}
                                        </button>
                                    )}
                                     //--> Key on strategyId: chart survives across backtest runs
                                     //--> within the same strategy, updating in-place via setData().
                                     //--> Only remounts when switching to a different strategy.
                                     key={`chart-${strategyId || 'new'}`}
                                     timeframe={activeTimeframe}
                                     //! F5 — rollover_days is a live MDMS stitch instruction; gate it on
                                     //! isContinuous the same way instrumentConfig?.isContinuous is read
                                     //! at :1206 — a PINNED instrument must never send a nonzero value.
                                     rolloverDays={instrumentConfig?.isContinuous ? (Number(instrumentConfig?.rolloverDays) || 0) : 0}
                                     //! LB-8 (F-06) — structural companion to rolloverDays above: thread the
                                     //! raw flag itself so resolveRolloverDays() can gate structurally.
                                     isContinuous={Boolean(instrumentConfig?.isContinuous)}
                                     layers={memoizedLayers}
                                     externalIndicators={strategyIndicators}
                                     id={strategyId || 'default'}
                                     nodes={effectiveNodes}
                                     edges={edges}
                                     chartType={activeChartType}
                                     availableTypes={null}
                                     onChartTypeChange={setActiveChartType}
                                     onTimeframeChange={handleChartTimeframeChange}
                                     heightClass="h-full"
                                     user={user}
                                     instrument={selectedInstrument}
                                     instruments={currentInstruments}
                                     general={chartGeneral}
                                     dateRange={effectiveDateRange}
                                     fetchDateRange={memoizedFetchDateRange}
                                     liveTick={liveTick}
                                     mode="rd"
                                     replayMode={(backtestResults?.runId || urlRunId) ? 'backtest_replay' : null}
                                     replayRunId={backtestResults?.runId || urlRunId || null}
                                     onBacktestRequired={handleBacktestRequired}
                                     tradeMarkers={tradeMarkers}
                                     levelHistory={tradesWithLevels}
                                     levelDisplayNodes={exitNodes}
                                     onCrosshairMove={handleCrosshairMove}
                                     onDataLoaded={handleDataLoaded}
                                     onError={handleChartError}
                                     onIndicatorsUpdate={setIndicatorMaps}
                                  />
                            ) : (
                               <div className="w-full h-full flex items-center justify-center text-fg-3 text-sm">
                                   <div className="text-center">
                                       <FiBarChart2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                       Select an instrument to view chart
                                   </div>
                               </div>
                           )}
                       </div>
                   </motion.div>
                   
                   {/* Bottom Dashboard Section (Automatically fills remaining space) */}
                   <motion.div
                        initial={false}
                        animate={{
                            flex: isChartExpanded ? 0 : 1,
                            opacity: isChartExpanded ? 0 : 1,
                            pointerEvents: isChartExpanded ? 'none' : 'auto',
                        }}
                        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                        className="w-full overflow-hidden"
                        style={{ minHeight: isChartExpanded ? 0 : undefined }}
                   >
                        <RDWatchDashboard
                            categorizedNodes={categorizedNodes}
                            latestData={latestData}
                            executionData={executionData}
                            liveNodeInvalidReasons={liveNodeInvalidReasons}
                            hoverData={hoverData}
                            allIndicatorMetadata={allIndicatorMetadata}
                            isLive={isDashboardLive}
                            onToggleLive={toggleDashboardLive}
                            onPopOut={handlePopOut}
                            onItemClick={handleDashboardItemClick}
                            selectedNodeId={selectedLogicNode?.id}
                            activeChartNodeId={visualLayers.find(l => l.type === activeChartType)?.id}
                            primaryTimeframe={activeTimeframe}
                            nodes={effectiveNodes}
                            edges={edges}
                            indicatorMaps={indicatorMaps}
                            visualOverrides={visualStore.effectiveOverrides}
                            backtestResults={backtestResults}
                            backtestConfig={backtestConfig}
                            onPeriodSelect={handlePeriodSelect}
                            isMetricsExpanded={isMetricsExpanded}
                            onToggleMetricsExpanded={handleToggleMetricsExpanded}
                        />
                   </motion.div>
                </div>

               {/* Canvas stays mounted across Flow, Tree, and R&D to preserve its viewport. */}
               <div
                 className="w-full h-full"
                 hidden={logicViewMode !== 'flow' || canvasSubView !== 'flow'}
               >
                 <StrategyLogicStep
                    selectedInstrument={selectedInstrument}
                    strategyInfo={strategyInfo}
                    onSelectNode={(node) => setSelectedLogicNode(node)}
                    selectedNodeId={selectedLogicNode?.id}
                    visible={logicViewMode === 'flow' && canvasSubView === 'flow'}
                    focusRequest={canvasFocusRequest}
                    nodes={canvasNodes}
                    edges={edges}
                    onNodesChange={handleNodesChange}
                    onEdgesChange={onEdgesChange}
                    setNodes={setNodes}
                    setEdges={setEdges}
                    allIndicatorMetadata={authorizedToolboxIndicators}
                    graphWiring={graphWiring}
                    outlineOpen={outlineOpen && canvasSubView === 'flow'}
                    outlineWidth={outlineWidth}
                    onOutlineWidthChange={setOutlineWidth}
                    toolbar={logicViewMode === 'flow' && canvasSubView === 'flow' ? (
                      <CanvasToolbar
                        subView="flow"
                        outlineOpen={outlineOpen}
                        onSubViewChange={handleCanvasSubViewChange}
                        onToggleOutline={() => setOutlineOpen(!outlineOpen)}
                        onArrange={handleAutoArrange}
                      />
                    ) : null}
                    outline={(
                      <StrategyTreeView
                        compact
                        model={builderTreeModel}
                        selectedId={selectedLogicNode?.id}
                        onSelect={handleSelectTreeAuthoringNode}
                        onLocate={handleLocateLogicNode}
                        graphWiring={graphWiring}
                        createNode={createNode}
                        updateNodeData={updateNodeData}
                        filteredIndicators={authorizedToolboxIndicators}
                        availableDataFields={availableDataFields}
                        allIndicatorMetadata={allIndicatorMetadata}
                        labelMode={labelMode}
                        onLabelModeChange={setLabelMode}
                        onDeleteNode={handleDeleteLogicNode}
                        className="h-full p-2"
                      />
                    )}
                 />
               </div>

               {logicViewMode === 'flow' && canvasSubView === 'tree' && (
                 <StrategyTreeView
                    model={builderTreeModel}
                    selectedId={selectedLogicNode?.id}
                    onSelect={handleSelectTreeAuthoringNode}
                    onLocate={handleLocateLogicNode}
                    graphWiring={graphWiring}
                    createNode={createNode}
                    updateNodeData={updateNodeData}
                    filteredIndicators={authorizedToolboxIndicators}
                    availableDataFields={availableDataFields}
                    allIndicatorMetadata={allIndicatorMetadata}
                    labelMode={labelMode}
                    onLabelModeChange={setLabelMode}
                    onDeleteNode={handleDeleteLogicNode}
                    className="h-full w-full p-3"
                    toolbar={(
                      <CanvasToolbar
                        subView="tree"
                        outlineOpen={outlineOpen}
                        onSubViewChange={handleCanvasSubViewChange}
                      />
                    )}
                 />
               )}
           </>
        )}

        {/* --- STEP 4: TRADE EXECUTION --- */}
        {activeStep === 'execution' && (
            <div className="w-full h-full animate-fadeIn overflow-auto p-4">
                <TradeExecutionStep
                    nodes={nodes}
                    edges={edges}
                    instrument={orderCardInstrument}
                    executionConfig={strategyInfo.executionConfig || {}}
                    strategyId={strategyId}
              onNodeExecutionChange={(nodeId, nextExecution) => {
                setNodes((prevNodes) => prevNodes.map((node) => {
                  if (node.id !== nodeId) return node;
                  return {
                    ...node,
                    data: {
                      ...(node.data || {}),
                      execution: {
                        ...(node.data?.execution || {}),
                        ...(nextExecution || {})
                      }
                    }
                  };
                }));
              }}
                    onConfigChange={(newConfig) => setStrategyInfo(prev => ({
                        ...prev,
                        executionConfig: { ...prev.executionConfig, ...newConfig }
                    }))}
                />
            </div>
        )}


      </div>
    );

  // Stable reference for chart timeframe changes to avoid SmartChart re-renders
  const handleChartTimeframeChangeRef = useRef(null);
  
  useEffect(() => {
    handleChartTimeframeChangeRef.current = (newTf) => {
        console.log('[StrategyBuilder] Timeframe changed from chart:', newTf);
        const dataSourceNode = nodes.find(n => n.type === 'data_source');
        if (dataSourceNode && dataSourceNode.data?.timeframe !== newTf) {
            updateNodeData(dataSourceNode.id, { timeframe: newTf });
        }
    };
  }, [nodes, updateNodeData]);

  const handleChartTimeframeChange = useCallback((newTf) => {
      if (handleChartTimeframeChangeRef.current) {
          handleChartTimeframeChangeRef.current(newTf);
      }
  }, []);

  if (isLoading) {
      return (
          <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-bg-3 dark:bg-bg-1">
              <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-fg-3 font-medium">Loading strategy...</p>
              </div>
          </div>
      );
  }

  return (
    <VisualOverridesContext.Provider value={visualOverridesContextValue}>
    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] w-full overflow-hidden lg:overflow-visible bg-bg-3 dark:bg-bg-1 text-fg-1">
      {/*>>> 50-8 AC7.5 / A-3 — the ONE inline notice (too large to save / record unavailable), shared with Live Analysis <<<*/}
      <VisualOverridesNotice store={visualStore} />
      {/* Pane 1: Visualization Canvas (Left/Center - Now Main) */}
      <div className="flex-1 bg-bg-3 dark:bg-bg-1 overflow-hidden flex flex-col relative">
         
         {/* Context Header (Dynamic based on progress) */}
         <div className="h-14 border-b border-[var(--divider)] bg-white/80 dark:bg-bg-1/80 backdrop-blur-md flex items-center px-4 justify-between shrink-0 z-10 shadow-sm relative overflow-hidden transition-all">
            
            <div className="flex items-center gap-3 z-10 w-full min-w-0">
               {/* Info strip — sections never wrap or shrink; scrolls horizontally only as a last resort */}
               <div className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto overflow-y-hidden [&>*]:shrink-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">

               {/* Strategy Identity Section - Clickable to Edit */}
               <Tooltip content="Click to edit details" placement="bottom">
               <div
                  className={`shrink-0 flex items-center gap-3 min-w-[200px] ${strategyInfo.name ? 'cursor-pointer group' : ''}`}
                  onClick={() => strategyInfo.name && setActiveStep('general')}
               >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-inner transition-all ${
 !strategyInfo.name 
                        ? 'bg-bg-3 dark:bg-bg-2 text-fg-3'
                        : strategyInfo.executionType === 'SEMI_AUTO'
                           ? 'bg-[color-mix(in_oklch,var(--node-math)_16%,transparent)] text-[var(--node-math)]'
                           : 'bg-[color-mix(in_oklch,var(--color-accent)_16%,transparent)] text-[var(--color-accent)]'
                  }`}>
                      {strategyInfo.executionType === 'SEMI_AUTO' ? (
                          <FiBell size={20} className={strategyInfo.name ? "fill-current/20" : ""} />
                      ) : (
                          <FiZap size={20} className={strategyInfo.name ? "fill-current/20" : ""} />
                      )}
                  </div>
                  
                  <div className="flex flex-col">
                     <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xl font-bold tracking-tight transition-colors ${
 strategyInfo.name ? 'text-fg-1' : 'text-fg-3 italic'
                        }`}>
                           {strategyInfo.name || 'Untitled Strategy'}
                        </span>
                                                {/* Dynamic Status Badge — persisted status from backend */}
                         {(() => {
                             // While a backtest is running, show transient "Backtesting" state
                             const displayStatus = isBacktesting
                                 ? 'backtesting'
                                 : (strategyInfo.status || 'draft');

                             const labels = {
                                 backtesting: 'Backtesting',
                                 draft: 'Draft',
                                 backtested: 'Backtested',
                                 paper: 'Paper Trade',
                                 live: 'Live Trade',
                                 stopped: 'Stopped',
                                 archived: 'Archived',
                             };

                             const styles = {
                                 backtesting: { variant: 'info',    pulse: true  },
                                 draft:       { variant: 'neutral', pulse: true  },
                                 backtested:  { variant: 'accent',  pulse: false },
                                 paper:       { variant: 'info',    pulse: true  },
                                 live:        { variant: 'profit',  pulse: true  },
                                 stopped:     { variant: 'warn',    pulse: false },
                                 archived:    { variant: 'loss',    pulse: false },
                             };

                             const s = styles[displayStatus] || styles.draft;
                             return (
                                 <span className={`ds-badge xs ${s.variant} font-bold uppercase tracking-wider transition-all duration-500`}>
                                    <span className={`ds-badge-dot ${s.pulse ? 'pulse' : ''}`}></span>
                                    {labels[displayStatus] ?? displayStatus}
                                 </span>
                             );
                         })()}
                      </div>
                     
                     <div className="flex items-center gap-3 text-xs text-fg-3 font-medium min-w-0">
                        <span className="shrink-0 flex items-center gap-1 bg-bg-3 dark:bg-bg-2/50 px-1.5 rounded text-[10px] font-mono border border-[var(--divider)]">
                           {strategyInfo?.version || 'v1.0'}
                        </span>
                        {strategyInfo.description && (
                           <Tooltip content={strategyInfo.description} placement="bottom">
                              <span className="truncate max-w-[200px]">
                                 {strategyInfo.description}
                              </span>
                           </Tooltip>
                        )}
                        {!strategyInfo.description && <span className="opacity-50 whitespace-nowrap">No description</span>}
                     </div>
                  </div>
               </div>
               </Tooltip>

               {/* Vertical Separator */}
               <div className="shrink-0 h-8 w-px bg-[var(--divider)]"></div>

               {/* Instrument Section (Compact) */}
               {/* Instrument Section (Compact) */}
               {selectedInstrument ? (
                 <div className="flex items-center gap-3 group cursor-pointer" onClick={() => setActiveStep('instruments')}>
                    {/* Asset Icon */}
                    <div className="w-10 h-10 rounded-xl bg-bg-3 dark:bg-bg-2 flex items-center justify-center text-fg-3 group-hover:bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] group-hover:text-[var(--node-visual)] transition-colors">
                        <FiBarChart2 size={20} />
                    </div>

                    <div className="flex flex-col">
                       <div className="flex items-center gap-2 mb-0.5">
                           <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Target Asset</span>
                           {instrumentConfig.isContinuous && (
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[color-mix(in_oklch,var(--node-math)_16%,transparent)] text-[var(--node-math)] font-bold border border-[color-mix(in_oklch,var(--node-math)_40%,transparent)] flex items-center gap-1">
                                 <FiActivity size={8} />
                                 CONT ({instrumentConfig.rolloverDays}d)
                              </span>
                           )}
                       </div>
                       
                       <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-fg-1 font-mono leading-none">
                             {selectedInstrument.symbol}
                          </span>
                          
                          <div className="flex items-center gap-1">
                             <span className="text-[9px] px-1.5 py-0.5 bg-bg-3 dark:bg-bg-2 border border-[var(--divider)] text-fg-2 dark:text-fg-3 rounded-md font-bold uppercase">
                                {selectedInstrument.exchange}
                             </span>
                             <span className="text-[9px] px-1.5 py-0.5 bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] text-[var(--node-visual)] rounded-md font-bold uppercase">
                                {selectedInstrument.instrument_type || selectedInstrument.type || 'EQ'}
                             </span>
                          </div>
                       </div>
                    </div>
                 </div>
               ) : (
                  <div className="flex flex-col opacity-40 cursor-pointer" onClick={() => setActiveStep('instruments')}>
                      <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Target Asset</span>
                      <span className="text-sm font-bold text-fg-3 italic">None Selected</span>
                  </div>
               )}

               {/* Vertical Separator */}
               <div className="shrink-0 h-8 w-px bg-[var(--divider)] hidden xl:block"></div>

               {/* Logic Section (Compact) */}
               {nodes && nodes.filter(n => ['indicator', 'math', 'comparator', 'logic_gate'].includes(n.type)).length > 0 ? (
                 <div className="hidden xl:flex items-center gap-3 group cursor-pointer" onClick={() => setActiveStep('logic')}>
                    <div className="w-10 h-10 rounded-xl bg-bg-3 dark:bg-bg-2 flex items-center justify-center text-fg-3 group-hover:bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] group-hover:text-[var(--node-comparator)] transition-colors">
                        <FiCpu size={20} />
                    </div>

                    <div className="flex flex-col">
                       <div className="flex items-center gap-2 mb-0.5">
                           <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Strategy Logic</span>
                       </div>
                       
                       <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-fg-1 font-mono leading-none">
                             {nodes.filter(n => ['indicator', 'math', 'comparator', 'logic_gate'].includes(n.type)).length} Nodes
                          </span>
                          
                          <div className="flex items-center gap-1">
                             <span className="text-[9px] px-1.5 py-0.5 bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] text-[var(--node-comparator)] rounded-md font-bold uppercase">
                                {nodes.filter(n => ['signal', 'position_trigger'].includes(n.type)).length} Signals
                             </span>
                          </div>
                       </div>
                    </div>
                 </div>
               ) : (
                  <div className="hidden xl:flex flex-col opacity-40 cursor-pointer" onClick={() => setActiveStep('logic')}>
                      <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Strategy Logic</span>
                      <span className="text-sm font-bold text-fg-3 italic">Not Configured</span>
                  </div>
               )}

                {/* Backtest Summary Section (Only when results available or backtesting) */}
                {(backtestResults || isBacktesting) && (
                  <>
                     {/* Vertical Separator */}
                     <div className="shrink-0 h-8 w-px bg-[var(--divider)] hidden 2xl:block"></div>

                     <div className="hidden 2xl:block">
                     <Tooltip content="Click to view backtest details" placement="bottom">
                     <div
                        className={`flex items-center gap-3 group cursor-pointer transition-all duration-500 overflow-hidden ${isBacktesting ? 'opacity-60 grayscale' : 'opacity-100'}`}
                        onClick={() => { setActiveStep('logic'); setLogicRightTab('backtest'); }}
                     >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
 isBacktesting
                            ? 'bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] text-[var(--node-visual)] animate-pulse'
                            : 'bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] text-[var(--node-comparator)] group-hover:bg-[color-mix(in_oklch,var(--node-comparator)_16%,transparent)] group-hover:text-[var(--node-comparator)]'
                        }`}>
                            <FiActivity size={20} />
                        </div>

                        <div className="flex flex-col">
                           <div className="flex items-center gap-1.5 mb-0.5">
                               <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Backtest Perf</span>
                               {backtestResults?.total_return_pct != null && (
                                   <div className={`flex items-center text-[8px] font-bold px-1 py-0.5 rounded ${backtestResults.total_return_pct >= 0 ? 'bg-[color-mix(in_oklch,var(--color-profit)_10%,transparent)] text-[var(--color-profit)]' : 'bg-[color-mix(in_oklch,var(--color-loss)_10%,transparent)] text-[var(--color-loss)]'}`}>
                                       {backtestResults.total_return_pct >= 0 ? <FiTrendingUp size={10} className="mr-0.5" /> : <FiTrendingDown size={10} className="mr-0.5" />}
                                       {Math.abs(backtestResults.total_return_pct).toFixed(2)}%
                                   </div>
                               )}
                           </div>

                           {isBacktesting ? (
                               <div className="flex flex-col gap-1 w-24">
                                   <div className="h-1 w-full bg-bg-3 rounded-full overflow-hidden">
                                       <motion.div
                                           className="h-full bg-[var(--node-visual)]"
                                           initial={{ width: 0 }}
                                           animate={{ width: `${backtestProgress}%` }}
                                       />
                                   </div>
                                   <span className="text-[10px] font-bold text-[var(--node-visual)] font-mono leading-none tracking-tight">
                                       {backtestStage} {Math.round(backtestProgress)}%
                                   </span>
                               </div>
                           ) : (
                               <div className="flex items-center gap-2">
                                  <span className={`text-sm font-bold font-mono leading-none ${(backtestResults?.total_return ?? 0) >= 0 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]'}`}>
                                     {(backtestResults?.total_return ?? 0) >= 0 ? '+' : ''}₹{Number(backtestResults?.total_return ?? 0).toLocaleString('en-IN')}
                                  </span>

                                  <div className="flex items-center gap-2 border-l border-[var(--divider)] pl-2">
                                     <div className="flex flex-col leading-none">
                                         <span className="text-[8px] text-fg-3 uppercase font-bold">Sharpe</span>
                                         <span className={`text-[10px] font-bold font-mono ${Number(backtestResults?.metrics?.sharpe_ratio || 0) >= 1 ? 'text-[var(--color-profit)]' : Number(backtestResults?.metrics?.sharpe_ratio || 0) >= 0 ? 'text-[var(--warning)]' : 'text-[var(--color-loss)]'}`}>{Number(backtestResults?.metrics?.sharpe_ratio || 0).toFixed(2)}</span>
                                     </div>
                                     <div className="flex flex-col leading-none">
                                         <span className="text-[8px] text-fg-3 uppercase font-bold">Win%</span>
                                         <span className={`text-[10px] font-bold font-mono ${Number(backtestResults?.metrics?.win_rate || 0) >= 50 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]'}`}>{Number(backtestResults?.metrics?.win_rate || 0).toFixed(1)}%</span>
                                     </div>
                                  </div>
                               </div>
                           )}
                        </div>
                     </div>
                     </Tooltip>
                     </div>
                  </>
                )}

               {/* Vertical Separator */}
               <div className="shrink-0 h-8 w-px bg-[var(--divider)] hidden 2xl:block"></div>

               {/* Execution Section (Compact) */}
               {strategyInfo.executionType ? (
                 <div className="hidden 2xl:flex items-center gap-3 group cursor-pointer" onClick={() => setActiveStep('execution')}>
                    <div className="w-10 h-10 rounded-xl bg-bg-3 dark:bg-bg-2 flex items-center justify-center text-fg-3 group-hover:bg-[color-mix(in_oklch,var(--color-profit)_10%,transparent)] group-hover:text-[var(--color-profit)] transition-colors">
                        <FiZap size={20} />
                    </div>

                    <div className="flex flex-col">
                       <div className="flex items-center gap-2 mb-0.5">
                           <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Review & Sizing</span>
                       </div>

                       <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-fg-1 font-mono leading-none">
                             {strategyInfo.executionType === 'SEMI_AUTO' ? 'Semi-Auto' : 'Automated'}
                          </span>

                          <div className="flex items-center gap-1">
                             <span className="text-[9px] px-1.5 py-0.5 bg-[color-mix(in_oklch,var(--color-profit)_10%,transparent)] text-[var(--color-profit)] rounded-md font-bold uppercase">
                                {strategyInfo.executionConfig?.brokerAccounts?.length || 0} Accounts
                             </span>
                          </div>
                       </div>
                    </div>
                 </div>
               ) : (
                  <div className="hidden 2xl:flex flex-col opacity-40 cursor-pointer" onClick={() => setActiveStep('execution')}>
                      <span className="text-[10px] uppercase font-bold text-fg-3 tracking-wider whitespace-nowrap">Review & Sizing</span>
                      <span className="text-sm font-bold text-fg-3 italic">Pending</span>
                  </div>
               )}

               </div>{/* end info strip */}

                  {/* View Controls (Right Side) — pinned right, outside the scroll strip */}
                  <div className="shrink-0 flex bg-bg-3 dark:bg-bg-2/80 rounded-lg p-1 border border-[var(--divider)] shadow-inner gap-1">
                   
                     {/* R&D Chart Toggle */}
                     <Tooltip content="Chart View (R&D)" placement="bottom">
                     <button
                       aria-label="Chart View (R&D)"
                       onClick={() => { setActiveStep('logic'); setLogicViewMode('rd'); }}
                       className={`flex items-center gap-1.5 p-2 rounded-md transition-all group overflow-hidden ${activeStep === 'logic' && logicViewMode === 'rd' ? 'bg-bg-3 shadow-sm text-[var(--node-visual)]' : 'text-fg-3 hover:bg-white dark:hover:bg-bg-3 hover:text-fg-2'}`}
                     >
                     <FiBarChart2 size={16} className="group-hover:scale-110 transition-transform" />
                     <motion.span 
                      initial={false}
                      animate={{ width: 'auto', opacity: 1 }}
                      className={`text-[10px] uppercase font-bold whitespace-nowrap overflow-hidden ${activeStep === 'logic' && logicViewMode === 'rd' ? 'block' : 'hidden group-hover:block'}`}
                     >
                      R&D Chart
                     </motion.span>
                     </button>
                     </Tooltip>

                     {/* GROUP: Canvas — Flow/Tree and Arrange now live inside the Canvas toolbar. */}
                     <div className="flex bg-bg-3/40 rounded-md p-0.5 gap-0.5">
                      {/* Logic Canvas Toggle */}
                      <Tooltip content="Logic Canvas" placement="bottom">
                      <button
                        aria-label="Logic Canvas"
                        aria-pressed={activeStep === 'logic' && logicViewMode === 'flow'}
                        onClick={() => {
                          setActiveStep('logic');
                          setLogicViewMode('flow');
                          if (isRightPaneCollapsed) setIsRightPaneCollapsed(false);
                        }}
                        className={`flex items-center gap-1.5 p-2 rounded-md transition-all group overflow-hidden ${activeStep === 'logic' && logicViewMode === 'flow' ? 'bg-bg-3 shadow-sm text-[var(--node-visual)]' : 'text-fg-3 hover:bg-white dark:hover:bg-bg-3 hover:text-fg-2'}`}
                      >
                        <FiCpu size={16} className="group-hover:scale-110 transition-transform" />
                        <motion.span 
                          initial={false}
                          animate={{ width: 'auto', opacity: 1 }}
                          className={`text-[10px] uppercase font-bold whitespace-nowrap overflow-hidden ${activeStep === 'logic' && logicViewMode === 'flow' ? 'block' : 'hidden group-hover:block'}`}
                        >
                          Canvas
                        </motion.span>
                      </button>
                      </Tooltip>
                     </div>

                     {/* Backtest View Quick Toggle */}
                     <Tooltip content="Backtest Configuration & History" placement="bottom">
                     <button
                       aria-label="Backtest Configuration & History"
                       onClick={() => {
                         setActiveStep('logic');
                         setLogicRightTab('backtest');
                         if (isRightPaneCollapsed) setIsRightPaneCollapsed(false);
                       }}
                       className={`flex items-center gap-1.5 p-2 rounded-md transition-all group overflow-hidden ${activeStep === 'logic' && logicRightTab === 'backtest' ? 'bg-bg-3 shadow-sm text-[var(--node-comparator)]' : 'text-fg-3 hover:bg-white dark:hover:bg-bg-3 hover:text-fg-2'}`}
                     >
                     <FiActivity size={16} className="group-hover:scale-110 transition-transform" />
                     <motion.span 
                      initial={false}
                      animate={{ width: 'auto', opacity: 1 }}
                      className={`text-[10px] uppercase font-bold whitespace-nowrap overflow-hidden ${activeStep === 'logic' && logicRightTab === 'backtest' ? 'block' : 'hidden group-hover:block'}`}
                     >
                      Backtest
                     </motion.span>
                     </button>
                     </Tooltip>

                     {/* Sidebar / Properties Toggle */}
                    <Tooltip content={isRightPaneCollapsed ? "Expand Properties" : "Collapse Properties"} placement="bottom">
                    <button
                      onClick={() => setIsRightPaneCollapsed(!isRightPaneCollapsed)}
                      aria-label={isRightPaneCollapsed ? "Expand Properties" : "Collapse Properties"}
                      className={`flex items-center gap-1.5 p-2 rounded-md transition-all group overflow-hidden ${isRightPaneCollapsed ? 'bg-[var(--node-visual)] text-white shadow-md' : 'text-fg-3 hover:bg-white dark:hover:bg-bg-3 hover:text-fg-2'}`}
                    >
                    <FiChevronRight size={18} className={`transition-transform duration-300 ${isRightPaneCollapsed ? 'rotate-180' : 'group-hover:scale-110'}`} />
                    <motion.span 
                      initial={false}
                      animate={{ width: 'auto', opacity: 1 }}
                      className={`text-[10px] uppercase font-bold whitespace-nowrap overflow-hidden ${!isRightPaneCollapsed ? 'block' : 'hidden group-hover:block'}`}
                     >
                      {isRightPaneCollapsed ? 'Expand' : 'Settings'}
                     </motion.span>
                    </button>
                    </Tooltip>

                   </div>
                </div>
               </div>
         
               <div className="flex-1 overflow-hidden relative">
                {renderCenterPaneContent()}
            
                {/* Floating Expand Arrow when sidebar is collapsed.
                    NOT wrapped in <Tooltip>: it's position:absolute and the .ds-tooltip
                    span would re-anchor it off the edge (aria-label + hover "Settings"
                    label describe it). */}
                {isRightPaneCollapsed && (
                  <button
                     onClick={() => setIsRightPaneCollapsed(false)}
                     className="absolute top-1/2 -translate-y-1/2 right-2 p-3 bg-white dark:bg-bg-2 border border-[var(--divider)] rounded-full shadow-xl hover:bg-accent/10 text-accent transition-all animate-fadeIn z-50 group hover:pr-5"
                     aria-label="Expand Properties Panel"
                  >
                     <div className="flex items-center gap-1">
                       <FiChevronRight size={24} className="rotate-180" />
                       <span className="text-[10px] font-bold uppercase tracking-tighter hidden group-hover:block transition-all">Settings</span>
                     </div>
                  </button>
                )}
               </div>
              </div>

      {/* Pane 3: Configuration (Right - Integrated Fluid Transition) */}
      <motion.div 
        initial={false}
        animate={{ 
          width: isRightPaneCollapsed ? 0 : 384, // 384 is w-96
          opacity: isRightPaneCollapsed ? 0 : 1,
          borderLeftWidth: isRightPaneCollapsed ? 0 : 1
        }}
        transition={{ 
          duration: 0.6, 
          ease: [0.4, 0, 0.2, 1] 
        }}
        className="bg-white dark:bg-bg-2 border-[var(--divider)] flex flex-col shadow-xl z-20 shrink-0 h-1/3 lg:h-auto overflow-hidden"
      >
         <div className="flex-1 overflow-y-auto p-6">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderRightPaneContent()}
            </motion.div>
         </div>
      </motion.div>
    </div>
    </VisualOverridesContext.Provider>
  );
};

export default StrategyBuilder;
